import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  addDoc,
  Timestamp,
  writeBatch,
  deleteField,
} from 'firebase/firestore';
import { getStorage, ref as storageRef, deleteObject, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import './AdminPortal.css';
import { exportOrdersAsCSV } from './utils/exportOrders';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { buildUploadedDocumentsZipBlob, uploadedDocBelongsToParent } from './utils/zipUploadedDocuments';
import {
  practiceDateToSortDate,
  isValidPracticeDate,
  practiceDateToHtmlDateValue,
  htmlDateValueToPracticeDate,
  localSortDateToday,
} from './utils/chalkTalk';
import { feeItemMarkedPaidFromFirestore } from './utils/feeItemPaid';
import ChalkTalkDrillNotes from './components/ChalkTalkDrillNotes';
import ChalkTalkDrillsAdminList from './components/ChalkTalkDrillsAdminList';
import PracticeLineBoard from './components/PracticeLineBoard';
import ParentSurveysAdmin from './components/ParentSurveysAdmin';
import TeamScheduleHighlights from './components/TeamScheduleHighlights';
import useUpcomingCalendarEvents from './hooks/useUpcomingCalendarEvents';
import {
  ALL_ADMIN_SECTION_IDS,
  OVERVIEW_SECTION_ID,
  STAFF_SECTION_ID,
  filterNavGroupsForStaff,
  expandAllowedSectionSet,
  isSuperStaffFromSections,
} from './utils/adminNavConfig';
import {
  DEFAULT_TEAM_STATS_TEAM_ID,
  normalizeTeamStatsTeamId,
  TEAM_STATS_TEAMS,
  getStatsCollectionRoot,
  getTeamStatsLabel,
  canAdminChooseStatsRoot,
} from './utils/teamStatsConfig';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from './firebase';
import { overrideDocId } from './utils/rosterCatalog';
import useRosterCatalog from './hooks/useRosterCatalog';
import RosterAdmin from './components/RosterAdmin';
import CoachesAdmin from './components/CoachesAdmin';
import SponsorsAdmin from './components/SponsorsAdmin';

const ADMIN_SECTION_STORAGE_KEY = 'adminPortalActiveSection';
const VALID_ADMIN_SECTIONS = new Set([OVERVIEW_SECTION_ID, STAFF_SECTION_ID, ...ALL_ADMIN_SECTION_IDS]);

const db = getFirestore();
const storage = getStorage();

/** Matches `DEFAULT_TEAM_DISCUSSION_ID` in Cloud Functions — cannot be deleted. */
const DEFAULT_TEAM_DISCUSSION_ID = 'general';

function formatDiscussionTimestamp(createdAt) {
  if (!createdAt) return '—';
  if (typeof createdAt.toDate === 'function') {
    try {
      return createdAt.toDate().toLocaleString();
    } catch (e) {
      /* ignore */
    }
  }
  const sec =
    typeof createdAt.seconds === 'number'
      ? createdAt.seconds
      : typeof createdAt._seconds === 'number'
        ? createdAt._seconds
        : null;
  if (sec != null) return new Date(sec * 1000).toLocaleString();
  return '—';
}

/** True when the doc looks like a real parent signup (PIN flow sets name/email). Empty rows are often Firebase users created only for Stripe (tryouts, Gladiator Games, merch) with no display name or email. */
function parentHasProfileFields(p) {
  const name = (p.name || p.displayName || '').trim();
  const email = (p.email || '').trim();
  return !!(name || email);
}

/** Active portal access only — disabled parents are excluded from discussion/DM-style pickers. */
function parentEligibleForTeamChat(p) {
  return parentHasProfileFields(p) && p.accessEnabled !== false;
}

/** Portal login enabled; missing `accessEnabled` is treated as on (legacy docs). */
function parentPortalEnabled(p) {
  return p.accessEnabled !== false;
}

/** Pool for Parent accounts table, line items, exports, and bulk targets (optionally includes portal-disabled). */
function parentsForAdminAccountsTable(parentsList, showIncompleteProfiles, showDisabledPortalParents) {
  let base = showIncompleteProfiles ? parentsList : parentsList.filter(parentHasProfileFields);
  if (!showDisabledPortalParents) {
    base = base.filter(parentPortalEnabled);
  }
  return base;
}

/** Avoid spamming the console when Firestore cannot reach the network (every getDoc would warn). */
function isBenignFirestoreReadError(err) {
  const code = err?.code;
  if (code === 'unavailable' || code === 'deadline-exceeded') return true;
  const msg = (typeof err?.message === 'string' ? err.message : '').toLowerCase();
  return msg.includes('offline') || msg.includes('failed to get document because the client is offline');
}

function feeItemFromDoc(d) {
  const x = d.data();
  return {
    id: d.id,
    description: x.description || '',
    amount: x.amount != null ? Number(x.amount) : 0,
    seasonLabel: x.seasonLabel || '',
    dueDate: typeof x.dueDate === 'string' ? x.dueDate : '',
    paid: feeItemMarkedPaidFromFirestore(x),
    paidAt: typeof x.paidAt === 'string' ? x.paidAt : '',
    paidNote: x.paidNote || '',
    paymentProofUrl: typeof x.paymentProofUrl === 'string' ? x.paymentProofUrl : '',
    _ms: x.createdAt?.toMillis?.() || 0,
  };
}

function sortFeeItemRows(rows) {
  const sorted = [...rows].sort((a, b) => a._ms - b._ms);
  return sorted.map(({ _ms, ...r }) => r);
}

function localYmdToday() {
  return new Date().toISOString().slice(0, 10);
}

function computeParentFeeSummary(items) {
  const list = Array.isArray(items) ? items : [];
  const today = localYmdToday();
  let balance = 0;
  let unpaidCount = 0;
  let lateCount = 0;
  let nextDue = '';
  for (const row of list) {
    const amt = Number(row.amount) || 0;
    if (!row.paid) {
      balance += amt;
      unpaidCount += 1;
      const dd = typeof row.dueDate === 'string' ? row.dueDate.trim() : '';
      if (dd) {
        if (dd < today) lateCount += 1;
        if (!nextDue || dd < nextDue) nextDue = dd;
      }
    }
  }
  return { balance, unpaidCount, lateCount, nextDue: nextDue || '' };
}

function daysLateForFeeItem(row, todayYmd) {
  if (row.paid) return 0;
  const dd = typeof row.dueDate === 'string' ? row.dueDate.trim() : '';
  if (!dd || dd >= todayYmd) return 0;
  const t = Date.parse(`${todayYmd}T12:00:00`);
  const d = Date.parse(`${dd}T12:00:00`);
  if (Number.isNaN(t) || Number.isNaN(d)) return 0;
  return Math.max(0, Math.floor((t - d) / 86400000));
}

const AdminPortal = () => {
  const { allOptions: rosterLinkOptions } = useRosterCatalog();
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  /** 'loading' | 'login' | 'ok' */
  const [adminGate, setAdminGate] = useState('loading');
  const [allowedSectionsArr, setAllowedSectionsArr] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [authNotice, setAuthNotice] = useState('');
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feeEditorParentId, setFeeEditorParentId] = useState(null);
  const [linkedPlayersEditorParentId, setLinkedPlayersEditorParentId] = useState(null);
  const [linkedPlayersDraftKeys, setLinkedPlayersDraftKeys] = useState([]);
  const [linkedPlayersSaving, setLinkedPlayersSaving] = useState(false);
  const [feeEditorRows, setFeeEditorRows] = useState([]);
  const [feeEditorLoading, setFeeEditorLoading] = useState(false);
  const [feeNewDescription, setFeeNewDescription] = useState('');
  const [feeNewAmount, setFeeNewAmount] = useState('');
  const [feeNewSeason, setFeeNewSeason] = useState('');
  const [feeNewDueDate, setFeeNewDueDate] = useState('');
  const [bulkFeeDescription, setBulkFeeDescription] = useState('');
  const [bulkFeeAmount, setBulkFeeAmount] = useState('');
  const [bulkFeeSeason, setBulkFeeSeason] = useState('');
  const [bulkFeeDueDate, setBulkFeeDueDate] = useState('');
  const [bulkFeeScope, setBulkFeeScope] = useState('profile');
  const [bulkFeeSkipDuplicates, setBulkFeeSkipDuplicates] = useState(true);
  const [bulkFeeApplying, setBulkFeeApplying] = useState(false);
  const [bulkFeeStatus, setBulkFeeStatus] = useState('');
  const [parentAccountsFilter, setParentAccountsFilter] = useState('');
  const [showIncompleteParentProfiles, setShowIncompleteParentProfiles] = useState(false);
  const [showDisabledPortalParents, setShowDisabledPortalParents] = useState(false);
  const [parentAccountsSort, setParentAccountsSort] = useState({ column: 'balance', direction: 'desc' });
  const [feeItemsByParentId, setFeeItemsByParentId] = useState({});
  const [duesOverviewLoading, setDuesOverviewLoading] = useState(false);
  const [duesOverviewError, setDuesOverviewError] = useState('');
  const [parentAccountsDuesView, setParentAccountsDuesView] = useState('families');
  const [lineItemsSort, setLineItemsSort] = useState({ column: 'dueDate', direction: 'asc' });
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [gallery, setGallery] = useState([]);

  const [chatDeleteDiscussionId, setChatDeleteDiscussionId] = useState('');
  const [chatDeleteMessageId, setChatDeleteMessageId] = useState('');
  const [chatDeleteBusy, setChatDeleteBusy] = useState(false);
  const [newDiscussionTitle, setNewDiscussionTitle] = useState('');
  const [newDiscussionTeamWide, setNewDiscussionTeamWide] = useState(true);
  const [newDiscussionMemberIds, setNewDiscussionMemberIds] = useState([]);
  const [creatingDiscussion, setCreatingDiscussion] = useState(false);
  const [newDiscussionParentSearch, setNewDiscussionParentSearch] = useState('');
  const [adminDiscussionsList, setAdminDiscussionsList] = useState([]);
  const [adminDiscussionsLoading, setAdminDiscussionsLoading] = useState(false);
  const [deletingDiscussionId, setDeletingDiscussionId] = useState(null);

  // Gladiator Games Registration States
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [registrationStats, setRegistrationStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    paid: 0
  });

  // Tryouts, Schedule Game, Join Us, Uploaded Documents
  const [tryouts, setTryouts] = useState([]);
  const [loadingTryouts, setLoadingTryouts] = useState(false);
  const [springHockeyRegs, setSpringHockeyRegs] = useState([]);
  const [loadingSpringHockey, setLoadingSpringHockey] = useState(false);
  const [scheduledGames, setScheduledGames] = useState([]);
  const [loadingScheduledGames, setLoadingScheduledGames] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [loadingUploadedDocs, setLoadingUploadedDocs] = useState(false);
  const [uploadedDocsZipBusy, setUploadedDocsZipBusy] = useState(false);
  const [uploadedDocsZipStatus, setUploadedDocsZipStatus] = useState('');
  const [selectedUploadDocIds, setSelectedUploadDocIds] = useState({});
  const [selectedParentIdsForDocZip, setSelectedParentIdsForDocZip] = useState({});
  const uploadedDocSelectAllRef = useRef(null);

  // Master record (game history) - stats from ended games
  const [gameHistory, setGameHistory] = useState([]);
  const [loadingGameHistory, setLoadingGameHistory] = useState(false);
  const [expandedGameId, setExpandedGameId] = useState(null);

  // Gladiator Gazette admin (recaps that can be pulled)
  const [gazetteEntries, setGazetteEntries] = useState([]);
  const [loadingGazettes, setLoadingGazettes] = useState(false);
  const [removingGazetteId, setRemovingGazetteId] = useState(null);
  /** Admin-only stats root (local can switch to production `stats` to clean leaked test games). */
  const [adminStatsRoot, setAdminStatsRoot] = useState(() => getStatsCollectionRoot());
  const [deletingGameKey, setDeletingGameKey] = useState(null);

  // Memory Game Leaderboard (Live Scoreboard)
  const [memoryLeaderboard, setMemoryLeaderboard] = useState([]);
  const [loadingMemoryLeaderboard, setLoadingMemoryLeaderboard] = useState(false);
  const [removingEntryId, setRemovingEntryId] = useState(null);

  // Gladiators TV: list of games (archived = YouTube URLs) for the remote
  const [gladiatorsTvGames, setGladiatorsTvGames] = useState([]);
  const [gladiatorsTvLiveStreamUrl, setGladiatorsTvLiveStreamUrl] = useState('');
  const [gladiatorsTvNewLabel, setGladiatorsTvNewLabel] = useState('');
  const [gladiatorsTvNewUrl, setGladiatorsTvNewUrl] = useState('');
  const [gladiatorsTvSaving, setGladiatorsTvSaving] = useState(false);
  const [gladiatorsTvLiveScoreboardTeamId, setGladiatorsTvLiveScoreboardTeamId] = useState(DEFAULT_TEAM_STATS_TEAM_ID);

  const [chalkTalkDrills, setChalkTalkDrills] = useState([]);
  const [loadingChalkTalkDrills, setLoadingChalkTalkDrills] = useState(false);
  const [chalkTalkSaving, setChalkTalkSaving] = useState(false);
  const [chalkTalkEditingId, setChalkTalkEditingId] = useState(null);
  const [chalkTalkFormDate, setChalkTalkFormDate] = useState('');
  const [chalkTalkFormName, setChalkTalkFormName] = useState('');
  const [chalkTalkFormVideoUrl, setChalkTalkFormVideoUrl] = useState('');
  const [chalkTalkFormNotes, setChalkTalkFormNotes] = useState('');
  const [chalkTalkFormImageUrl, setChalkTalkFormImageUrl] = useState('');
  const [chalkTalkImageFile, setChalkTalkImageFile] = useState(null);
  const [chalkTalkDrillAckers, setChalkTalkDrillAckers] = useState({});
  const [chalkTalkAckersLoading, setChalkTalkAckersLoading] = useState(false);
  const [chalkTalkExpandedAckDrillId, setChalkTalkExpandedAckDrillId] = useState(null);
  const [chalkTalkLibrary, setChalkTalkLibrary] = useState([]);
  const [loadingChalkTalkLibrary, setLoadingChalkTalkLibrary] = useState(false);
  const [chalkTalkRemoveTarget, setChalkTalkRemoveTarget] = useState(null);
  const [chalkTalkLibraryScheduleModal, setChalkTalkLibraryScheduleModal] = useState(null);
  const [chalkTalkLibraryScheduleDate, setChalkTalkLibraryScheduleDate] = useState('');

  const [staffList, setStaffList] = useState([]);
  const [staffListLoading, setStaffListLoading] = useState(false);
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffPassword, setNewStaffPassword] = useState('');
  const [newStaffDisplayName, setNewStaffDisplayName] = useState('');
  const [newStaffFullAccess, setNewStaffFullAccess] = useState(false);
  const [newStaffSectionPicks, setNewStaffSectionPicks] = useState(() => new Set(['overview', 'gladiatorGames']));
  const [staffFormBusy, setStaffFormBusy] = useState(false);
  const [editingStaffUid, setEditingStaffUid] = useState(null);
  const [editStaffActive, setEditStaffActive] = useState(true);
  const [editStaffFullAccess, setEditStaffFullAccess] = useState(false);
  const [editStaffSectionPicks, setEditStaffSectionPicks] = useState(() => new Set());
  const [editStaffPassword, setEditStaffPassword] = useState('');
  const [showNewStaffPassword, setShowNewStaffPassword] = useState(false);
  const [showEditStaffPassword, setShowEditStaffPassword] = useState(false);
  const [staffAccountsNotice, setStaffAccountsNotice] = useState(null);

  const [adminNavOpen, setAdminNavOpen] = useState(false);
  const [activeAdminSection, setActiveAdminSectionState] = useState(() => {
    try {
      const v = sessionStorage.getItem(ADMIN_SECTION_STORAGE_KEY);
      if (v && VALID_ADMIN_SECTIONS.has(v)) return v;
    } catch (e) {
      /* ignore */
    }
    return 'overview';
  });

  const [showArchived, setShowArchived] = useState({
    gladiatorGames: false,
    tryouts: false,
    springHockey: false,
    scheduledGames: false,
    submissions: false,
    uploadedDocs: false,
  });
  const {
    events: upcomingScheduleEvents,
    loading: loadingUpcomingScheduleEvents,
    error: upcomingScheduleEventsError,
    refresh: refreshUpcomingScheduleEvents,
    lastLoadedAt: upcomingScheduleLastLoadedAt,
    feedUrls: teamScheduleFeedUrls,
  } = useUpcomingCalendarEvents();

  const setActiveSection = useCallback((id) => {
    setActiveAdminSectionState(id);
    try {
      sessionStorage.setItem(ADMIN_SECTION_STORAGE_KEY, id);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const allowedSectionSet = useMemo(() => new Set(allowedSectionsArr), [allowedSectionsArr]);
  const filteredNavGroups = useMemo(
    () => filterNavGroupsForStaff(allowedSectionSet),
    [allowedSectionSet]
  );

  const reloadDuesFeeOverview = useCallback(async () => {
    setDuesOverviewLoading(true);
    setDuesOverviewError('');
    try {
      const ids = parents.map((p) => p.id).filter(Boolean);
      if (ids.length === 0) {
        setFeeItemsByParentId({});
        return;
      }
      const byId = {};
      let cursor = 0;
      const concurrency = Math.min(12, Math.max(1, ids.length));
      const worker = async () => {
        for (;;) {
          const i = cursor;
          cursor += 1;
          if (i >= ids.length) return;
          const parentId = ids[i];
          try {
            const snap = await getDocs(collection(db, 'parents', parentId, 'feeItems'));
            const rows = snap.docs.map(feeItemFromDoc);
            if (rows.length > 0) {
              byId[parentId] = sortFeeItemRows(rows);
            }
          } catch (err) {
            console.warn('Could not load feeItems for parent', parentId, err);
          }
        }
      };
      await Promise.all(Array.from({ length: concurrency }, () => worker()));
      setFeeItemsByParentId(byId);
    } catch (e) {
      console.error(e);
      setDuesOverviewError('Failed to load dues overview.');
      setFeeItemsByParentId({});
    } finally {
      setDuesOverviewLoading(false);
    }
  }, [parents]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setAuthNotice('');
      if (!user) {
        setAdminGate('login');
        setAllowedSectionsArr([]);
        setIsSuperAdmin(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'adminUsers', user.uid));
        if (!snap.exists() || snap.data()?.active === false) {
          setAuthNotice('This account is not set up for admin access.');
          await signOut(auth);
          setAdminGate('login');
          return;
        }
        const data = snap.data();
        const expanded = expandAllowedSectionSet(data?.sections);
        setAllowedSectionsArr([...expanded]);
        setIsSuperAdmin(isSuperStaffFromSections(data?.sections));
        setAdminGate('ok');
      } catch (err) {
        console.error('Admin staff profile load failed:', err);
        setAuthNotice('Could not verify staff access. Try again.');
        try {
          await signOut(auth);
        } catch (e2) {
          /* ignore */
        }
        setAdminGate('login');
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (adminGate !== 'ok' || allowedSectionsArr.length === 0) return;
    if (!allowedSectionSet.has(activeAdminSection)) {
      const next = allowedSectionSet.has(OVERVIEW_SECTION_ID)
        ? OVERVIEW_SECTION_ID
        : allowedSectionsArr.find((id) => allowedSectionSet.has(id)) || OVERVIEW_SECTION_ID;
      setActiveSection(next);
    }
  }, [adminGate, allowedSectionsArr, allowedSectionSet, activeAdminSection, setActiveSection]);

  const chalkTalkDrillIdsKey = useMemo(() => {
    const ids = chalkTalkDrills.map((d) => d.id).filter(Boolean).sort();
    return ids.join('\n');
  }, [chalkTalkDrills]);

  /** Parents who can use the portal — same pool as team chat (excludes portal-disabled and no-profile). */
  const chalkTalkParentIdsKey = useMemo(() => {
    const ids = parents.filter(parentEligibleForTeamChat).map((p) => p.id).filter(Boolean).sort();
    return ids.join('\n');
  }, [parents]);

  useEffect(() => {
    if (adminGate !== 'ok' || activeAdminSection !== 'chalkTalk' || !allowedSectionSet.has('chalkTalk')) {
      return undefined;
    }
    let cancelled = false;
    const drillIds = chalkTalkDrillIdsKey ? chalkTalkDrillIdsKey.split('\n') : [];
    const parentIds = chalkTalkParentIdsKey ? chalkTalkParentIdsKey.split('\n') : [];
    if (drillIds.length === 0) {
      setChalkTalkDrillAckers({});
      setChalkTalkAckersLoading(false);
      return undefined;
    }
    (async () => {
      setChalkTalkAckersLoading(true);
      try {
        // Collection-group + documentId() requires a full path per Firestore; use per-parent getDoc instead.
        const entries = await Promise.all(
          drillIds.map(async (drillId) => {
            const list = [];
            await Promise.all(
              parentIds.map(async (parentId) => {
                try {
                  const snap = await getDoc(doc(db, 'parents', parentId, 'chalkTalkDrillAcks', drillId));
                  if (snap.exists()) {
                    list.push({ parentId, at: snap.data().acknowledgedAt });
                  }
                } catch (readErr) {
                  if (!isBenignFirestoreReadError(readErr)) {
                    console.warn('Chalk Talk ack read:', parentId, drillId, readErr);
                  }
                }
              })
            );
            return [drillId, list];
          })
        );
        if (cancelled) return;
        setChalkTalkDrillAckers(Object.fromEntries(entries));
      } catch (e) {
        if (!isBenignFirestoreReadError(e)) {
          console.error('Chalk Talk drill acks:', e);
        }
        if (!cancelled) setChalkTalkDrillAckers({});
      } finally {
        if (!cancelled) setChalkTalkAckersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminGate, activeAdminSection, allowedSectionSet, chalkTalkDrillIdsKey, chalkTalkParentIdsKey]);

  const chalkTalkDrillsGrouped = useMemo(() => {
    const order = [];
    const map = new Map();
    for (const d of chalkTalkDrills) {
      const key = (d.practiceDate || '—').trim() || '—';
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key).push(d);
    }
    const daySortDesc = (k) => {
      if (k === '—') return '\u0000';
      return practiceDateToSortDate(k) || '\u0000';
    };
    order.sort((a, b) => daySortDesc(b).localeCompare(daySortDesc(a)));
    return order.map((k) => ({
      practiceDateKey: k,
      rows: (map.get(k) || []).slice().sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)),
    }));
  }, [chalkTalkDrills]);

  const [chalkTalkCopySourceKey, setChalkTalkCopySourceKey] = useState('');
  const [chalkTalkCopyTargetHtml, setChalkTalkCopyTargetHtml] = useState(() => localSortDateToday());

  const assignableSectionIdsForForms = useMemo(() => {
    const base = [...ALL_ADMIN_SECTION_IDS];
    if (isSuperAdmin) base.push(STAFF_SECTION_ID);
    return base;
  }, [isSuperAdmin]);

  const loadStaffList = useCallback(async () => {
    setStaffListLoading(true);
    try {
      const res = await httpsCallable(functions, 'listStaffUsers')();
      setStaffList(res.data?.users || []);
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Failed to load staff list.');
    } finally {
      setStaffListLoading(false);
    }
  }, []);

  const loadAdminDiscussions = useCallback(async () => {
    setAdminDiscussionsLoading(true);
    try {
      const res = await httpsCallable(functions, 'listDiscussionsAdmin')();
      const list = Array.isArray(res.data?.discussions) ? res.data.discussions : [];
      setAdminDiscussionsList(list);
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Failed to load discussions.');
    } finally {
      setAdminDiscussionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminGate !== 'ok' || activeAdminSection !== STAFF_SECTION_ID) return;
    loadStaffList();
  }, [adminGate, activeAdminSection, loadStaffList]);

  useEffect(() => {
    if (adminGate !== 'ok' || activeAdminSection !== 'parentChat' || !allowedSectionSet.has('parentChat')) {
      return;
    }
    loadAdminDiscussions();
  }, [adminGate, activeAdminSection, allowedSectionSet, loadAdminDiscussions]);

  useEffect(() => {
    if (!staffAccountsNotice) return undefined;
    const t = setTimeout(() => setStaffAccountsNotice(null), 6500);
    return () => clearTimeout(t);
  }, [staffAccountsNotice]);

  const editingStaffRecord = useMemo(() => {
    if (!editingStaffUid) return null;
    return staffList.find((u) => u.uid === editingStaffUid) || null;
  }, [editingStaffUid, staffList]);

  /** Aggregate player stats across all games in master record. Key by normalized name only so the same player across games (e.g. different or missing jersey #) counts as one. */
  const aggregatedRoster = useMemo(() => {
    const byKey = {};
    const normalizeName = (n) => (n || '').trim().toLowerCase();
    gameHistory.forEach(game => {
      const lineupList = Array.isArray(game.lineup) ? game.lineup : [];
      const gd = game.gameData || {};
      lineupList.forEach(p => {
        const key = normalizeName(p.name) || '—';
        if (!byKey[key]) {
          byKey[key] = {
            name: (p.name || '—').trim(),
            number: p.number ?? '—',
            position: p.position || '—',
            isGoalie: !!p.isGoalie,
            gamesPlayed: 0,
            goals: 0,
            assists: 0,
            points: 0,
            plusMinus: 0,
            penalties: 0,
            pim: 0,
            saves: 0,
            goalsAgainst: 0,
          };
        }
        byKey[key].gamesPlayed += 1;
        if (p.isGoalie) {
          byKey[key].saves += gd.goalieSaves ?? 0;
          byKey[key].goalsAgainst += gd.score?.them ?? 0;
        } else {
          byKey[key].goals += p.goals ?? 0;
          byKey[key].assists += p.assists ?? 0;
          byKey[key].points += p.points ?? 0;
          byKey[key].plusMinus += p.plusMinus ?? 0;
          byKey[key].penalties += p.penalties ?? 0;
          byKey[key].pim += Number(p.pim) || 0;
        }
        if (p.number != null && p.number !== '' && (byKey[key].number === '—' || byKey[key].number === '')) {
          byKey[key].number = p.number;
        }
        if (p.position && (!byKey[key].position || byKey[key].position === '—')) {
          byKey[key].position = p.position;
        }
      });
    });
    const list = Object.values(byKey).filter((p) => p.name !== '—');
    list.sort((a, b) => {
      if (a.isGoalie && !b.isGoalie) return 1;
      if (!a.isGoalie && b.isGoalie) return -1;
      return (b.points || 0) - (a.points || 0) || a.name.localeCompare(b.name);
    });
    return list;
  }, [gameHistory]);

  const parentAccountsTableParents = useMemo(
    () => parentsForAdminAccountsTable(parents, showIncompleteParentProfiles, showDisabledPortalParents),
    [parents, showIncompleteParentProfiles, showDisabledPortalParents]
  );

  const parentAccountsPoolSize = useMemo(() => parentAccountsTableParents.length, [parentAccountsTableParents]);

  const filteredSortedParents = useMemo(() => {
    let list = parentAccountsTableParents.map((p) => p);
    const q = parentAccountsFilter.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const name = (p.name || p.displayName || '').toLowerCase();
        const email = (p.email || '').toLowerCase();
        const stripeId = (p.stripeCustomerId || '').toLowerCase();
        const id = (p.id || '').toLowerCase();
        return (
          name.includes(q) ||
          email.includes(q) ||
          stripeId.includes(q) ||
          id.includes(q)
        );
      });
    }
    const { column: col, direction: dir } = parentAccountsSort;
    const mult = dir === 'asc' ? 1 : -1;
    const feeSummary = (p) => {
      const lines =
        feeEditorParentId === p.id && !feeEditorLoading ? feeEditorRows : feeItemsByParentId[p.id];
      return computeParentFeeSummary(lines);
    };
    list.sort((a, b) => {
      let cmp = 0;
      if (col === 'portal') {
        const pa = a.accessEnabled === false ? 0 : 1;
        const pb = b.accessEnabled === false ? 0 : 1;
        cmp = dir === 'asc' ? pb - pa : pa - pb;
      } else if (col === 'name') {
        cmp =
          mult *
          (a.name || a.displayName || '').localeCompare(b.name || b.displayName || '', undefined, {
            sensitivity: 'base',
          });
      } else if (col === 'email') {
        cmp = mult * (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' });
      } else if (col === 'balance') {
        cmp = mult * (feeSummary(a).balance - feeSummary(b).balance);
      } else if (col === 'late') {
        cmp = mult * (feeSummary(a).lateCount - feeSummary(b).lateCount);
      } else if (col === 'nextDue') {
        const na = feeSummary(a).nextDue || '9999-12-31';
        const nb = feeSummary(b).nextDue || '9999-12-31';
        cmp = mult * na.localeCompare(nb);
      }
      if (cmp !== 0) return cmp;
      return (a.id || '').localeCompare(b.id || '');
    });
    return list;
  }, [
    parentAccountsTableParents,
    parentAccountsFilter,
    parentAccountsSort,
    feeItemsByParentId,
    feeEditorParentId,
    feeEditorLoading,
    feeEditorRows,
  ]);

  const filteredSortedFlatLineItems = useMemo(() => {
    const today = localYmdToday();
    const q = parentAccountsFilter.trim().toLowerCase();
    const parentAllowed = new Set(parentAccountsTableParents.map((p) => p.id));
    const parentById = {};
    for (const p of parents) {
      if (p.id) parentById[p.id] = p;
    }
    const rows = [];
    for (const [pid, items] of Object.entries(feeItemsByParentId)) {
      if (!parentAllowed.has(pid)) continue;
      const p = parentById[pid];
      if (!p) continue;
      for (const item of items) {
        if (q) {
          const name = (p.name || p.displayName || '').toLowerCase();
          const email = (p.email || '').toLowerCase();
          const desc = (item.description || '').toLowerCase();
          const season = (item.seasonLabel || '').toLowerCase();
          const stripeId = (p.stripeCustomerId || '').toLowerCase();
          const idLower = (p.id || '').toLowerCase();
          if (
            !name.includes(q) &&
            !email.includes(q) &&
            !desc.includes(q) &&
            !season.includes(q) &&
            !stripeId.includes(q) &&
            !idLower.includes(q)
          ) {
            continue;
          }
        }
        rows.push({
          key: `${pid}-${item.id}`,
          parent: p,
          item,
          daysLate: daysLateForFeeItem(item, today),
        });
      }
    }
    const { column: col, direction: dir } = lineItemsSort;
    const mult = dir === 'asc' ? 1 : -1;
    const parentLabel = (parent) =>
      (parent.name || parent.displayName || parent.email || parent.id || '').trim();
    rows.sort((a, b) => {
      let cmp = 0;
      if (col === 'parentName') {
        cmp =
          mult *
          parentLabel(a.parent).localeCompare(parentLabel(b.parent), undefined, { sensitivity: 'base' });
      } else if (col === 'description') {
        cmp =
          mult *
          (a.item.description || '').localeCompare(b.item.description || '', undefined, { sensitivity: 'base' });
      } else if (col === 'amount') {
        cmp = mult * ((Number(a.item.amount) || 0) - (Number(b.item.amount) || 0));
      } else if (col === 'dueDate') {
        const da = (a.item.dueDate || '').trim() || '9999-12-31';
        const db = (b.item.dueDate || '').trim() || '9999-12-31';
        cmp = mult * da.localeCompare(db);
      } else if (col === 'paid') {
        cmp = mult * ((a.item.paid ? 1 : 0) - (b.item.paid ? 1 : 0));
      } else if (col === 'daysLate') {
        cmp = mult * (a.daysLate - b.daysLate);
      }
      if (cmp !== 0) return cmp;
      return (a.item.id || '').localeCompare(b.item.id || '');
    });
    return rows;
  }, [parents, feeItemsByParentId, parentAccountsFilter, parentAccountsTableParents, lineItemsSort]);

  const parentsEligibleForTeamChat = useMemo(() => {
    const list = parents.filter(parentEligibleForTeamChat);
    list.sort((a, b) =>
      (a.name || a.displayName || '').localeCompare(b.name || b.displayName || '', undefined, {
        sensitivity: 'base',
      })
    );
    return list;
  }, [parents]);

  const filteredNewDiscussionParents = useMemo(() => {
    const q = newDiscussionParentSearch.trim().toLowerCase();
    if (!q) return parentsEligibleForTeamChat;
    return parentsEligibleForTeamChat.filter((p) => {
      const name = (p.name || p.displayName || '').toLowerCase();
      const email = (p.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [parentsEligibleForTeamChat, newDiscussionParentSearch]);

  const bulkFeeTargets = useMemo(() => {
    if (bulkFeeScope === 'table') {
      return filteredSortedParents;
    }
    return parents.filter(
      (p) => parentHasProfileFields(p) && (showDisabledPortalParents || parentPortalEnabled(p))
    );
  }, [bulkFeeScope, parents, filteredSortedParents, showDisabledPortalParents]);

  const visibleUploadedDocs = useMemo(
    () => (showArchived.uploadedDocs ? uploadedDocs : uploadedDocs.filter((r) => !r.archived)),
    [showArchived.uploadedDocs, uploadedDocs]
  );

  const parentsWithUploadedForms = useMemo(() => {
    const ids = new Set();
    for (const doc of visibleUploadedDocs) {
      for (const p of parents) {
        if (uploadedDocBelongsToParent(doc, p)) {
          ids.add(p.id);
          break;
        }
      }
    }
    return parents
      .filter((p) => ids.has(p.id) && (showDisabledPortalParents || parentPortalEnabled(p)))
      .sort((a, b) =>
        (a.name || a.displayName || '').localeCompare(b.name || b.displayName || '', undefined, {
          sensitivity: 'base',
        })
      );
  }, [parents, visibleUploadedDocs, showDisabledPortalParents]);

  const selectedVisibleUploadCount = useMemo(
    () => visibleUploadedDocs.filter((r) => selectedUploadDocIds[r.id]).length,
    [visibleUploadedDocs, selectedUploadDocIds]
  );

  const rowsForSelectedParentsZip = useMemo(() => {
    const sp = parents.filter((p) => selectedParentIdsForDocZip[p.id]);
    if (sp.length === 0) return [];
    return visibleUploadedDocs.filter((doc) => sp.some((p) => uploadedDocBelongsToParent(doc, p)));
  }, [visibleUploadedDocs, parents, selectedParentIdsForDocZip]);

  useEffect(() => {
    const el = uploadedDocSelectAllRef.current;
    if (!el) return;
    const n = visibleUploadedDocs.length;
    el.indeterminate = selectedVisibleUploadCount > 0 && selectedVisibleUploadCount < n;
  }, [selectedVisibleUploadCount, visibleUploadedDocs.length]);

  const parentsWithProfileCount = useMemo(
    () =>
      parents.filter(
        (p) => parentHasProfileFields(p) && (showDisabledPortalParents || parentPortalEnabled(p))
      ).length,
    [parents, showDisabledPortalParents]
  );

  const reloadChalkTalkDrills = useCallback(async () => {
    try {
      setLoadingChalkTalkDrills(true);
      const snap = await getDocs(collection(db, 'chalkTalkDrills'));
      const data = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      data.sort((a, b) => {
        const sa = a.sortDate || practiceDateToSortDate(a.practiceDate) || '';
        const sb = b.sortDate || practiceDateToSortDate(b.practiceDate) || '';
        if (sa !== sb) return sa.localeCompare(sb);
        return (Number(a.order) || 0) - (Number(b.order) || 0);
      });
      setChalkTalkDrills(data);
    } catch (err) {
      console.error('Error fetching Chalk Talk drills:', err);
    } finally {
      setLoadingChalkTalkDrills(false);
    }
  }, []);

  const reloadChalkTalkLibrary = useCallback(async () => {
    try {
      setLoadingChalkTalkLibrary(true);
      const snap = await getDocs(collection(db, 'chalkTalkDrillLibrary'));
      const data = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      data.sort((a, b) => {
        const ta = a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
        const tb = b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
        return tb - ta;
      });
      setChalkTalkLibrary(data);
    } catch (err) {
      console.error('Error fetching Chalk Talk drill library:', err);
    } finally {
      setLoadingChalkTalkLibrary(false);
    }
  }, []);

  useEffect(() => {
    if (adminGate !== 'ok' || activeAdminSection !== 'chalkTalk' || !allowedSectionSet.has('chalkTalk')) {
      return undefined;
    }
    reloadChalkTalkLibrary();
    return undefined;
  }, [adminGate, activeAdminSection, allowedSectionSet, reloadChalkTalkLibrary]);

  useEffect(() => {
    if (adminGate !== 'ok') return;

    const fetchPin = async () => {
      try {
        const pinSnap = await getDoc(doc(db, 'settings', 'parentPin'));
        if (pinSnap.exists()) {
          setCurrentPin(pinSnap.data().code);
        }
      } catch (err) {
        console.error('Error fetching PIN:', err);
      }
    };

    const fetchParents = async () => {
      try {
        const snap = await getDocs(collection(db, 'parents'));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setParents(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching parents:', err);
      }
    };

    const fetchGallery = async () => {
      try {
        const snap = await getDocs(collection(db, 'gallery'));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setGallery(data);
      } catch (err) {
        console.error('Error fetching gallery:', err);
      }
    };

    const fetchRegistrations = async () => {
      try {
        setLoadingRegistrations(true);
        const snap = await getDocs(collection(db, 'gladiator-games-registrations'));
        const data = snap.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        data.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
        setRegistrations(data);
        const active = data.filter(r => !r.archived);
        setRegistrationStats({
          total: active.length,
          pending: active.filter(r => r.status === 'pending').length,
          confirmed: active.filter(r => r.status === 'confirmed').length,
          paid: active.filter(r => r.status === 'paid').length
        });
        setLoadingRegistrations(false);
      } catch (err) {
        console.error('Error fetching registrations:', err);
        setLoadingRegistrations(false);
      }
    };

    const toDate = (v) => {
      if (!v) return null;
      if (v && typeof v.toDate === 'function') return v.toDate();
      return new Date(v);
    };

    const fetchTryouts = async () => {
      try {
        setLoadingTryouts(true);
        const snap = await getDocs(collection(db, 'tryouts'));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (toDate(b.submittedAt) || 0) - (toDate(a.submittedAt) || 0));
        setTryouts(data);
      } catch (err) {
        console.error('Error fetching tryouts:', err);
      } finally {
        setLoadingTryouts(false);
      }
    };

    const fetchSpringHockey = async () => {
      try {
        setLoadingSpringHockey(true);
        const snap = await getDocs(collection(db, 'spring_hockey_registrations'));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (toDate(b.submittedAt) || 0) - (toDate(a.submittedAt) || 0));
        setSpringHockeyRegs(data);
      } catch (err) {
        console.error('Error fetching spring hockey registrations:', err);
      } finally {
        setLoadingSpringHockey(false);
      }
    };

    const fetchScheduledGames = async () => {
      try {
        setLoadingScheduledGames(true);
        const snap = await getDocs(collection(db, 'scheduledGames'));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (toDate(b.submittedAt) || 0) - (toDate(a.submittedAt) || 0));
        setScheduledGames(data);
      } catch (err) {
        console.error('Error fetching scheduled games:', err);
      } finally {
        setLoadingScheduledGames(false);
      }
    };

    const fetchSubmissions = async () => {
      try {
        setLoadingSubmissions(true);
        const snap = await getDocs(collection(db, 'submissions'));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (toDate(b.submittedAt) || 0) - (toDate(a.submittedAt) || 0));
        setSubmissions(data);
      } catch (err) {
        console.error('Error fetching submissions:', err);
      } finally {
        setLoadingSubmissions(false);
      }
    };

    const fetchUploadedDocuments = async () => {
      try {
        setLoadingUploadedDocs(true);
        const snap = await getDocs(collection(db, 'uploaded_documents'));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (toDate(b.timestamp) || 0) - (toDate(a.timestamp) || 0));
        setUploadedDocs(data);
      } catch (err) {
        console.error('Error fetching uploaded documents:', err);
      } finally {
        setLoadingUploadedDocs(false);
      }
    };

    const fetchGameHistory = async () => {
      try {
        setLoadingGameHistory(true);
        const root = adminStatsRoot;
        const all = [];
        for (const team of TEAM_STATS_TEAMS) {
          const snap = await getDocs(collection(db, root, team.teamId, 'gameHistory'));
          snap.docs.forEach((d) => {
            all.push({
              id: d.id,
              teamId: team.teamId,
              teamLabel: team.label,
              ...d.data(),
            });
          });
        }
        all.sort((a, b) => new Date(b.endedAt || 0) - new Date(a.endedAt || 0));
        setGameHistory(all);
      } catch (err) {
        console.error('Error fetching game history:', err);
      } finally {
        setLoadingGameHistory(false);
      }
    };

    const fetchGazettes = async () => {
      try {
        setLoadingGazettes(true);
        const root = adminStatsRoot;
        const all = [];
        for (const team of TEAM_STATS_TEAMS) {
          const snap = await getDocs(collection(db, root, team.teamId, 'gameHistory'));
          snap.docs.forEach((d) => {
            const data = d.data() || {};
            if (!data.recap?.headline) return;
            all.push({
              id: d.id,
              teamId: team.teamId,
              teamLabel: team.label,
              ...data,
            });
          });
        }
        all.sort((a, b) => new Date(b.endedAt || b.details?.date || 0) - new Date(a.endedAt || a.details?.date || 0));
        setGazetteEntries(all);
      } catch (err) {
        console.error('Error fetching Gazettes:', err);
      } finally {
        setLoadingGazettes(false);
      }
    };

    const fetchGladiatorsTv = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'gladiatorsTv'));
        if (snap.exists()) {
          const data = snap.data();
          if (Array.isArray(data.games)) setGladiatorsTvGames(data.games);
          setGladiatorsTvLiveStreamUrl(typeof data.liveStreamUrl === 'string' ? data.liveStreamUrl : '');
          setGladiatorsTvLiveScoreboardTeamId(normalizeTeamStatsTeamId(data.liveScoreboardTeamId));
        }
      } catch (err) {
        console.error('Error fetching Gladiators TV games:', err);
      }
    };

    const fetchMemoryLeaderboard = async () => {
      try {
        setLoadingMemoryLeaderboard(true);
        const teamId = 'glass-city-gladiators-12u';
        const colRef = collection(doc(db, adminStatsRoot, teamId), 'memoryLeaderboard');
        const snap = await getDocs(colRef);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (a.moves || 0) - (b.moves || 0) || (a.timeSeconds || 0) - (b.timeSeconds || 0));
        setMemoryLeaderboard(data);
      } catch (err) {
        console.error('Error fetching memory leaderboard:', err);
      } finally {
        setLoadingMemoryLeaderboard(false);
      }
    };

    fetchPin();
    fetchParents();
    fetchGallery();
    fetchRegistrations();
    fetchTryouts();
    fetchSpringHockey();
    fetchScheduledGames();
    fetchSubmissions();
    fetchUploadedDocuments();
    fetchGameHistory();
    fetchGazettes();
    fetchGladiatorsTv();
    fetchMemoryLeaderboard();
    reloadChalkTalkDrills();
  }, [adminGate, reloadChalkTalkDrills, adminStatsRoot]);

  useEffect(() => {
    if (!feeEditorParentId) return;
    const visible = new Set(filteredSortedParents.map((p) => p.id));
    if (!visible.has(feeEditorParentId)) setFeeEditorParentId(null);
  }, [filteredSortedParents, feeEditorParentId]);

  useEffect(() => {
    if (adminGate !== 'ok' || activeAdminSection !== 'parentAccounts' || !allowedSectionSet.has('parentAccounts')) {
      return undefined;
    }
    void reloadDuesFeeOverview();
    return undefined;
  }, [adminGate, activeAdminSection, allowedSectionSet, reloadDuesFeeOverview]);

  const handleExportOrders = async () => {
    try {
      await exportOrdersAsCSV();
    } catch (err) {
      alert('Failed to export orders.');
      console.error(err);
    }
  };

  const handleExportDuesLineItemsCSV = () => {
    const today = localYmdToday();
    const baseParents = parentsForAdminAccountsTable(
      parents,
      showIncompleteParentProfiles,
      showDisabledPortalParents
    );
    const parentAllowed = new Set(baseParents.map((p) => p.id));
    const parentById = {};
    for (const p of parents) {
      if (p.id) parentById[p.id] = p;
    }
    const rows = [];
    for (const [pid, items] of Object.entries(feeItemsByParentId)) {
      if (!parentAllowed.has(pid)) continue;
      const p = parentById[pid];
      if (!p) continue;
      const portal = p.accessEnabled === false ? 'Disabled' : 'Active';
      for (const item of items) {
        rows.push({
          parentId: pid,
          parentName: (p.name || p.displayName || '').trim(),
          parentEmail: (p.email || '').trim(),
          portalAccess: portal,
          lineItemId: item.id,
          description: item.description || '',
          amount: item.amount,
          seasonLabel: item.seasonLabel || '',
          dueDate: item.dueDate || '',
          paid: item.paid ? 'Yes' : 'No',
          paidAt: item.paidAt || '',
          paidNote: item.paidNote || '',
          paymentProofUrl: item.paymentProofUrl || '',
          daysLate: daysLateForFeeItem(item, today),
        });
      }
    }
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(blob, `dues-line-items-${stamp}.csv`);
  };

  const handleExportDuesFamilySummaryCSV = () => {
    const baseParents = parentsForAdminAccountsTable(
      parents,
      showIncompleteParentProfiles,
      showDisabledPortalParents
    );
    const rows = baseParents.map((p) => {
      const s = computeParentFeeSummary(feeItemsByParentId[p.id]);
      const portal = p.accessEnabled === false ? 'Disabled' : 'Active';
      return {
        parentId: p.id,
        parentName: (p.name || p.displayName || '').trim(),
        parentEmail: (p.email || '').trim(),
        portalAccess: portal,
        balanceOwed: s.balance,
        unpaidLineCount: s.unpaidCount,
        lateLineCount: s.lateCount,
        nextDueDate: s.nextDue || '',
      };
    });
    rows.sort((a, b) => (Number(b.balanceOwed) || 0) - (Number(a.balanceOwed) || 0));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(blob, `dues-family-summary-${stamp}.csv`);
  };

  const handleExportGameHistoryGamesCSV = () => {
    if (!gameHistory.length) {
      alert('No games to export.');
      return;
    }
    const rows = gameHistory.map((row) => {
      const details = row.details || {};
      const gd = row.gameData || {};
      const score = gd.score || {};
      const shots = gd.shots || {};
      const lineupList = Array.isArray(row.lineup) ? row.lineup : [];
      const skaters = lineupList.filter((p) => !p.isGoalie);
      const goalie = lineupList.find((p) => p.isGoalie);
      const saves = gd.goalieSaves ?? 0;
      const ga = score.them ?? 0;
      const shotsAgainst = saves + ga;
      const savePct = shotsAgainst > 0 ? ((saves / shotsAgainst) * 100).toFixed(2) : '';
      return {
        gameId: row.id,
        team: row.teamLabel || getTeamStatsLabel(row.teamId) || '',
        teamId: row.teamId || '',
        date: details.date ? new Date(details.date).toLocaleDateString() : '',
        opponent: details.opponent || '',
        location: details.location || '',
        homeAway: details.homeAway || details.homeOrAway || '',
        scoreUs: score.us ?? 0,
        scoreThem: score.them ?? 0,
        shotsUs: shots.us ?? 0,
        shotsThem: shots.them ?? 0,
        skaters: skaters.length,
        goalie: goalie?.name || '',
        goalieSaves: saves,
        goalsAgainst: ga,
        savePct,
        endedAt: row.endedAt ? new Date(row.endedAt).toLocaleString() : '',
        showOnHome: row.showOnHome !== false ? 'Yes' : 'No',
        hasGazette: row.recap?.headline ? 'Yes' : 'No',
        gazetteHeadline: row.recap?.headline || '',
        statsRoot: adminStatsRoot,
      };
    });
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(blob, `game-history-games-${adminStatsRoot}-${stamp}.csv`);
  };

  const handleExportGameHistoryPlayersCSV = () => {
    if (!gameHistory.length) {
      alert('No games to export.');
      return;
    }
    const rows = [];
    gameHistory.forEach((row) => {
      const details = row.details || {};
      const gd = row.gameData || {};
      const score = gd.score || {};
      const lineupList = Array.isArray(row.lineup) ? row.lineup : [];
      const team = row.teamLabel || getTeamStatsLabel(row.teamId) || '';
      const gameDate = details.date ? new Date(details.date).toLocaleDateString() : '';
      const opponent = details.opponent || '';
      lineupList.forEach((p) => {
        const isGoalie = !!p.isGoalie;
        const saves = isGoalie ? (gd.goalieSaves ?? 0) : '';
        const ga = isGoalie ? (score.them ?? 0) : '';
        const shotsAgainst = isGoalie ? (Number(saves) || 0) + (Number(ga) || 0) : 0;
        const savePct =
          isGoalie && shotsAgainst > 0 ? (((Number(saves) || 0) / shotsAgainst) * 100).toFixed(2) : '';
        rows.push({
          gameId: row.id,
          team,
          teamId: row.teamId || '',
          date: gameDate,
          opponent,
          playerName: (p.name || '').trim(),
          number: p.number ?? '',
          position: isGoalie ? 'G' : p.position || '',
          isGoalie: isGoalie ? 'Yes' : 'No',
          goals: isGoalie ? '' : p.goals ?? 0,
          assists: isGoalie ? '' : p.assists ?? 0,
          points: isGoalie ? '' : p.points ?? 0,
          plusMinus: isGoalie ? '' : p.plusMinus ?? 0,
          penalties: isGoalie ? '' : p.penalties ?? 0,
          pim: isGoalie ? '' : p.pim != null ? Number(p.pim) : 0,
          saves,
          goalsAgainst: ga,
          savePct,
          scoreUs: score.us ?? 0,
          scoreThem: score.them ?? 0,
          endedAt: row.endedAt ? new Date(row.endedAt).toLocaleString() : '',
          statsRoot: adminStatsRoot,
        });
      });
    });
    if (!rows.length) {
      alert('No player lineup data to export.');
      return;
    }
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(blob, `game-history-players-${adminStatsRoot}-${stamp}.csv`);
  };

  const handleExportGameHistorySeasonTotalsCSV = () => {
    if (!aggregatedRoster.length) {
      alert('No season totals to export.');
      return;
    }
    const rows = aggregatedRoster.map((p) => {
      const shotsAgainst = (p.saves || 0) + (p.goalsAgainst || 0);
      const savePct =
        p.isGoalie && shotsAgainst > 0 ? (((p.saves || 0) / shotsAgainst) * 100).toFixed(2) : '';
      return {
        playerName: p.name,
        number: p.number,
        position: p.isGoalie ? 'G' : p.position,
        isGoalie: p.isGoalie ? 'Yes' : 'No',
        gamesPlayed: p.gamesPlayed,
        goals: p.isGoalie ? '' : p.goals,
        assists: p.isGoalie ? '' : p.assists,
        points: p.isGoalie ? '' : p.points,
        plusMinus: p.isGoalie ? '' : p.plusMinus,
        penalties: p.isGoalie ? '' : p.penalties,
        pim: p.isGoalie ? '' : Number(p.pim || 0),
        saves: p.isGoalie ? p.saves : '',
        goalsAgainst: p.isGoalie ? p.goalsAgainst : '',
        savePct,
        statsRoot: adminStatsRoot,
      };
    });
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(blob, `game-history-season-totals-${adminStatsRoot}-${stamp}.csv`);
  };

  const handleExportTryouts = () => {
    const headers = [
      'Submitted At',
      'Status',
      'Parent First Name',
      'Parent Last Name',
      'Email',
      'Phone',
      'Participant Name',
      'Birth Year',
      'Position',
      'Current Hockey Situation',
      'Current Team',
      'Looking For From Gladiators',
      'Years of Experience',
      'Excited About',
      'Future Events',
      'Program Importance',
      'How Did You Hear',
      'Fee'
    ];

    const activeTryouts = tryouts.filter(r => !r.archived);
    const escapeField = (field) => {
      if (field == null) return '';
      const str = String(field);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.join(','),
      ...activeTryouts.map(row => {
        const submittedAt = row.submittedAt
          ? (typeof row.submittedAt?.toDate === 'function'
            ? row.submittedAt.toDate()
            : new Date(row.submittedAt)).toLocaleString()
          : '';
        return [
          escapeField(submittedAt),
          escapeField(row.status),
          escapeField(row.parentFirstName),
          escapeField(row.parentLastName),
          escapeField(row.email),
          escapeField(row.phone),
          escapeField(row.participantName || row.skaterName),
          escapeField(row.birthYear),
          escapeField(row.position),
          escapeField(row.currentHockeySituation),
          escapeField(row.currentTeam),
          escapeField(row.lookingForFromGladiators),
          escapeField(row.yearsOfExperience),
          escapeField(Array.isArray(row.excitedAbout) ? row.excitedAbout.join('; ') : row.excitedAbout),
          escapeField(row.futureEvents),
          escapeField(Array.isArray(row.programImportance) ? row.programImportance.join('; ') : row.programImportance),
          escapeField(row.howDidYouHear || row.heardFrom),
          escapeField(row.fee)
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tryouts-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportSpringHockey = () => {
    const headers = [
      'Submitted At',
      'Parent Email',
      'Parent Name',
      'Player',
      'Position',
      'Jersey #',
      'Backup #',
      'Jersey Size',
      'T-Shirt',
      'Sweatshirt',
      'Warmup Size (full)',
      'Warmup Fit (Men/Youth)',
      'Warmup Type',
      'Warmup Jacket Letter',
      'Pant Fit (Men/Youth)',
      'Pant Size',
      'Glove Category',
      'Glove Size (in)',
      'Photo Permission',
      'Photo URL',
      'Bio',
      'Fun Fact',
    ];
    const escapeField = (field) => {
      if (field == null) return '';
      const str = String(field);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const fmtDate = (row) => {
      const v = row.submittedAt;
      if (v && typeof v.toDate === 'function') return v.toDate().toLocaleString();
      return v ? new Date(v).toLocaleString() : '';
    };
    const active = springHockeyRegs.filter(r => !r.archived);
    const csvContent = [
      headers.join(','),
      ...active.map(row =>
        [
          escapeField(fmtDate(row)),
          escapeField(row.parentEmail),
          escapeField(row.parentDisplayName),
          escapeField(row.playerName),
          escapeField(row.position),
          escapeField(row.desiredNumber),
          escapeField(row.backupDesiredNumber),
          escapeField(row.jerseySize),
          escapeField(row.tShirtSize),
          escapeField(row.sweatshirtSize),
          escapeField(row.warmupJacketPick),
          escapeField(row.warmupJacketFit),
          escapeField(row.warmupJacketType),
          escapeField(row.warmupJacketSize),
          escapeField(row.pantsFit),
          escapeField(row.pantsSize),
          escapeField(row.gloveCategory),
          escapeField(row.gloveSizeInches),
          escapeField(row.rosterPhotoPermissionAcknowledged ? 'yes' : ''),
          escapeField(row.rosterPhotoUrl),
          escapeField(row.bio),
          escapeField(row.funFact),
        ].join(',')
      ),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `spring-hockey-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportRegistrations = () => {
    const headers = [
      'Submitted At',
      'Status',
      'Parent First Name',
      'Parent Last Name',
      'Email',
      'Phone',
      'Skater Name',
      'Birth Year',
      'Position',
      'Current Hockey Situation',
      'Years of Experience',
      'Excited About',
      'Future Events Interest',
      'Program Importance',
      'How Did You Hear',
      'Additional Tickets',
      'Ticket Count'
    ];

    const activeRegs = registrations.filter(r => !r.archived);
    const csvContent = [
      headers.join(','),
      ...activeRegs.map(reg => {
        // Helper function to escape CSV fields that contain commas or quotes
        const escapeField = (field) => {
          if (field == null) return '';
          const str = String(field);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        };

        return [
          escapeField(new Date(reg.submittedAt).toLocaleString()),
          escapeField(reg.status),
          escapeField(reg.parentFirstName),
          escapeField(reg.parentLastName),
          escapeField(reg.email),
          escapeField(reg.phone),
          escapeField(reg.skaterName),
          escapeField(reg.birthYear),
          escapeField(reg.position),
          escapeField(reg.currentHockeySituation || 'N/A'),
          escapeField(reg.yearsOfExperience || 'N/A'),
          escapeField(Array.isArray(reg.excitedAbout) ? reg.excitedAbout.join('; ') : 'N/A'),
          escapeField(reg.futureEvents || 'N/A'),
          escapeField(Array.isArray(reg.programImportance) ? reg.programImportance.join('; ') : 'N/A'),
          escapeField(reg.howDidYouHear || 'N/A'),
          escapeField(reg.wantsAdditionalTickets || 'No'),
          escapeField(reg.additionalTicketsCount || '0')
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `gladiator-games-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleUpdateRegistrationStatus = async (regId, newStatus) => {
    try {
      const regRef = doc(db, 'gladiator-games-registrations', regId);
      await updateDoc(regRef, { status: newStatus });
      
      // Update local state
      setRegistrations(prev => prev.map(r => 
        r.id === regId ? { ...r, status: newStatus } : r
      ));
      
      // Update stats
      const updatedRegs = registrations.map(r => 
        r.id === regId ? { ...r, status: newStatus } : r
      );
      const stats = {
        total: updatedRegs.length,
        pending: updatedRegs.filter(r => r.status === 'pending').length,
        confirmed: updatedRegs.filter(r => r.status === 'confirmed').length,
        paid: updatedRegs.filter(r => r.status === 'paid').length
      };
      setRegistrationStats(stats);
      
      alert('Status updated successfully!');
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status.');
    }
  };

  const handleDeleteRegistration = async (regId) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;
    
    try {
      await deleteDoc(doc(db, 'gladiator-games-registrations', regId));
      setRegistrations(prev => prev.filter(r => r.id !== regId));
      
      // Update stats
      const updatedRegs = registrations.filter(r => r.id !== regId);
      const stats = {
        total: updatedRegs.length,
        pending: updatedRegs.filter(r => r.status === 'pending').length,
        confirmed: updatedRegs.filter(r => r.status === 'confirmed').length,
        paid: updatedRegs.filter(r => r.status === 'paid').length
      };
      setRegistrationStats(stats);
      
      alert('Registration deleted successfully!');
    } catch (err) {
      console.error('Error deleting registration:', err);
      alert('Failed to delete registration.');
    }
  };

  const handleDeleteImage = async (photoId, imageUrl) => {
    const confirmed = window.confirm('Are you sure you want to delete this photo?');
    if (!confirmed) return;

    try {
      let path;
      try {
        path = decodeURIComponent(new URL(imageUrl).pathname.split('/o/')[1].split('?')[0]);
      } catch (e) {
        console.warn('⚠️ Failed to parse image URL path:', e);
        return;
      }

      const fileRef = storageRef(storage, path);
      await deleteObject(fileRef);
      console.log('✅ Image deleted from Firebase Storage');
    } catch (err) {
      console.warn('⚠️ Storage delete issue:', err.message);
    }

    try {
      if (!photoId || typeof photoId !== 'string' || photoId.trim() === '') {
        throw new Error(`Invalid or missing photoId: ${photoId}`);
      }

      const docRef = doc(db, 'gallery', photoId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.warn(`📄 Document not found in Firestore for ID: ${photoId}`);
        setGallery(prev => prev.filter(p => p.id !== photoId));
        return;
      }

      await deleteDoc(docRef);
      console.log('✅ Firestore metadata deleted');
      setGallery(prev => prev.filter(p => p.id !== photoId));
      alert('Photo deleted successfully!');
    } catch (err) {
      console.error('❌ Failed to delete photo metadata from Firestore:', err);
      alert('Failed to delete photo from gallery metadata.');
    }
  };

  const handlePinUpdate = async () => {
    if (!newPin) {
      alert('Enter a new PIN first.');
      return;
    }
    try {
      const pinRef = doc(db, 'settings', 'parentPin');
      await setDoc(pinRef, { code: newPin }, { merge: true });
      setCurrentPin(newPin);
      setNewPin('');
      alert('Parent PIN created/updated!');
    } catch (err) {
      console.error('Error setting PIN:', err);
      alert('Failed to set PIN.');
    }
  };

  const handleSetParentPortalAccess = async (parentId, parentName, enabled) => {
    const label = parentName || 'this family';
    const msg = enabled
      ? `Allow parent portal login again for ${label}? They can sign in with their existing email and password.`
      : `Disable parent portal access for ${label}? They will be signed out on next load and cannot use team dues, documents, or other portal sections until you turn access back on.`;
    if (!window.confirm(msg)) return;
    try {
      await updateDoc(doc(db, 'parents', parentId), { accessEnabled: enabled });
      setParents((ps) =>
        ps.map((p) => (p.id === parentId ? { ...p, accessEnabled: enabled } : p))
      );
    } catch (err) {
      console.error(err);
      alert('Failed to update portal access.');
    }
  };

  const openLinkedPlayersEditor = (parent) => {
    if (linkedPlayersEditorParentId === parent.id) {
      setLinkedPlayersEditorParentId(null);
      setLinkedPlayersDraftKeys([]);
      return;
    }
    const existingKeys = Array.isArray(parent.linkedPlayerKeys)
      ? parent.linkedPlayerKeys.filter((k) => typeof k === 'string')
      : Array.isArray(parent.linkedPlayers)
        ? parent.linkedPlayers
            .map((lp) =>
              lp && lp.teamId && lp.playerName ? overrideDocId(lp.teamId, lp.playerName) : null
            )
            .filter(Boolean)
        : [];
    setLinkedPlayersDraftKeys(existingKeys);
    setLinkedPlayersEditorParentId(parent.id);
    setFeeEditorParentId(null);
  };

  const toggleLinkedPlayerDraftKey = (key) => {
    setLinkedPlayersDraftKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSaveLinkedPlayers = async (parentId) => {
    setLinkedPlayersSaving(true);
    try {
      const linkedPlayers = rosterLinkOptions.filter((p) =>
        linkedPlayersDraftKeys.includes(p.key)
      ).map((p) => ({
        teamId: p.teamId,
        playerName: p.playerName,
        key: p.key,
      }));
      const linkedPlayerKeys = linkedPlayers.map((lp) => lp.key || overrideDocId(lp.teamId, lp.playerName));
      await updateDoc(doc(db, 'parents', parentId), { linkedPlayers, linkedPlayerKeys });
      setParents((ps) =>
        ps.map((p) => (p.id === parentId ? { ...p, linkedPlayers, linkedPlayerKeys } : p))
      );
      setLinkedPlayersEditorParentId(null);
      setLinkedPlayersDraftKeys([]);
    } catch (err) {
      console.error(err);
      alert('Failed to save linked roster players.');
    } finally {
      setLinkedPlayersSaving(false);
    }
  };

  useEffect(() => {
    if (adminGate !== 'ok' || !feeEditorParentId) {
      setFeeEditorRows([]);
      return undefined;
    }
    let cancelled = false;
    setFeeEditorLoading(true);
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'parents', feeEditorParentId, 'feeItems'));
        if (cancelled) return;
        const rows = snap.docs.map(feeItemFromDoc);
        setFeeEditorRows(sortFeeItemRows(rows));
      } catch (e) {
        console.error(e);
        alert('Failed to load fee line items.');
      } finally {
        if (!cancelled) setFeeEditorLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminGate, feeEditorParentId]);

  const patchFeeRow = (id, patch) => {
    setFeeEditorRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleSaveFeeRow = async (row) => {
    if (!feeEditorParentId || !row.id) return;
    try {
      const isPaid = feeItemMarkedPaidFromFirestore({ paid: row.paid });
      const paidAt =
        isPaid && (row.paidAt || new Date().toISOString().slice(0, 10));
      await updateDoc(doc(db, 'parents', feeEditorParentId, 'feeItems', row.id), {
        description: (row.description || '').trim(),
        amount: parseFloat(row.amount) || 0,
        seasonLabel: (row.seasonLabel || '').trim(),
        dueDate: typeof row.dueDate === 'string' ? row.dueDate.trim() : '',
        paid: isPaid,
        paidAt: isPaid ? paidAt : '',
        paidNote: (row.paidNote || '').trim(),
      });
      await reloadDuesFeeOverview();
    } catch (err) {
      console.error(err);
      alert('Failed to save line item.');
    }
  };

  const handleAddFeeItem = async () => {
    if (!feeEditorParentId) return;
    const description = feeNewDescription.trim();
    const amount = parseFloat(feeNewAmount);
    if (!description || Number.isNaN(amount)) {
      alert('Enter a description and valid amount.');
      return;
    }
    try {
      await addDoc(collection(db, 'parents', feeEditorParentId, 'feeItems'), {
        description,
        amount,
        seasonLabel: (feeNewSeason || '').trim(),
        dueDate: (feeNewDueDate || '').trim(),
        paid: false,
        paidAt: '',
        paidNote: '',
        createdAt: Timestamp.now(),
      });
      setFeeNewDescription('');
      setFeeNewAmount('');
      setFeeNewSeason('');
      setFeeNewDueDate('');
      const snap = await getDocs(collection(db, 'parents', feeEditorParentId, 'feeItems'));
      const rows = snap.docs.map(feeItemFromDoc);
      setFeeEditorRows(sortFeeItemRows(rows));
      await reloadDuesFeeOverview();
    } catch (err) {
      console.error(err);
      alert('Failed to add line item.');
    }
  };

  /**
   * @param {string} itemId - feeItems doc id
   * @param {string|null} [parentId] - parent uid; omit when deleting from the open fee editor (uses feeEditorParentId)
   * @param {string|null} [confirmMessage] - optional confirm text (e.g. from All line items view)
   */
  const handleDeleteFeeItem = async (itemId, parentId = null, confirmMessage = null) => {
    const pid = parentId != null && parentId !== '' ? parentId : feeEditorParentId;
    if (!pid || !itemId) return;
    const msg =
      confirmMessage ||
      'Delete this line item? Parents will no longer see it.';
    if (!window.confirm(msg)) return;
    try {
      await deleteDoc(doc(db, 'parents', pid, 'feeItems', itemId));
      if (feeEditorParentId === pid) {
        setFeeEditorRows((rows) => rows.filter((r) => r.id !== itemId));
      }
      await reloadDuesFeeOverview();
    } catch (err) {
      console.error(err);
      alert('Failed to delete line item.');
    }
  };

  const FIRESTORE_BATCH_LIMIT = 500;

  const handleBulkApplyFeeItems = async () => {
    const description = bulkFeeDescription.trim();
    const amount = parseFloat(bulkFeeAmount);
    if (!description || Number.isNaN(amount)) {
      alert('Enter a description and valid amount for bulk apply.');
      return;
    }
    const targets = bulkFeeTargets;
    if (targets.length === 0) {
      alert('No families match the selected scope.');
      return;
    }
    const seasonNorm = (bulkFeeSeason || '').trim();
    const dueDateNorm = (bulkFeeDueDate || '').trim();
    const descNorm = description.toLowerCase();
    const scopeLabel =
      bulkFeeScope === 'profile'
        ? 'all families with name/email'
        : 'families currently shown in the table (filter + sort)';
    const dupNote = bulkFeeSkipDuplicates
      ? ' Families that already have the same description and season label will be skipped.'
      : ' Duplicate lines may be created if a family already has this charge.';
    if (
      !window.confirm(
        `Add one unpaid line item to ${targets.length} famil${targets.length === 1 ? 'y' : 'ies'}?\n\n` +
          `Scope: ${scopeLabel}.\n` +
          `${description} — $${amount}${seasonNorm ? ` (${seasonNorm})` : ''}${dueDateNorm ? ` — due ${dueDateNorm}` : ''}.${dupNote}`
      )
    ) {
      return;
    }

    setBulkFeeApplying(true);
    setBulkFeeStatus('Checking existing lines…');
    try {
      let parentIdsToWrite;
      if (bulkFeeSkipDuplicates) {
        const list = [...targets];
        let cursor = 0;
        const concurrency = Math.min(12, Math.max(1, list.length));
        const acc = [];
        const worker = async () => {
          for (;;) {
            const i = cursor;
            cursor += 1;
            if (i >= list.length) return;
            const p = list[i];
            const snap = await getDocs(collection(db, 'parents', p.id, 'feeItems'));
            const exists = snap.docs.some((d) => {
              const x = d.data();
              return (
                (x.description || '').trim().toLowerCase() === descNorm &&
                (x.seasonLabel || '').trim() === seasonNorm
              );
            });
            if (!exists) acc.push(p.id);
          }
        };
        await Promise.all(Array.from({ length: concurrency }, () => worker()));
        parentIdsToWrite = acc;
      } else {
        parentIdsToWrite = targets.map((p) => p.id);
      }

      if (parentIdsToWrite.length === 0) {
        setBulkFeeStatus('');
        alert(
          `No new lines were added. All ${targets.length} selected famil${targets.length === 1 ? 'y' : 'ies'} already had this line (same description and season label).`
        );
        return;
      }

      const skipped = targets.length - parentIdsToWrite.length;
      const linePayload = {
        description,
        amount,
        seasonLabel: seasonNorm,
        dueDate: dueDateNorm,
        paid: false,
        paidAt: '',
        paidNote: '',
        createdAt: Timestamp.now(),
      };

      setBulkFeeStatus(`Writing ${parentIdsToWrite.length} line item(s)…`);
      for (let i = 0; i < parentIdsToWrite.length; i += FIRESTORE_BATCH_LIMIT) {
        const slice = parentIdsToWrite.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const batch = writeBatch(db);
        for (const parentId of slice) {
          const ref = doc(collection(db, 'parents', parentId, 'feeItems'));
          batch.set(ref, linePayload);
        }
        await batch.commit();
      }

      const added = parentIdsToWrite.length;
      setBulkFeeStatus('');
      await reloadDuesFeeOverview();
      alert(
        `Done. Added the line to ${added} famil${added === 1 ? 'y' : 'ies'}.` +
          (skipped > 0 ? ` Skipped ${skipped} (already had this line).` : '')
      );
    } catch (err) {
      console.error(err);
      setBulkFeeStatus('');
      alert('Bulk apply failed. Check the console and your connection, then try again.');
    } finally {
      setBulkFeeApplying(false);
    }
  };

  const resetChalkTalkForm = () => {
    setChalkTalkEditingId(null);
    setChalkTalkFormDate('');
    setChalkTalkFormName('');
    setChalkTalkFormVideoUrl('');
    setChalkTalkFormNotes('');
    setChalkTalkFormImageUrl('');
    setChalkTalkImageFile(null);
  };

  const startEditChalkTalkDrill = (row) => {
    setChalkTalkEditingId(row.id);
    setChalkTalkFormDate(row.practiceDate || '');
    setChalkTalkFormName(row.name || '');
    setChalkTalkFormVideoUrl(row.videoUrl || row.link || '');
    setChalkTalkFormNotes(row.notes || '');
    setChalkTalkFormImageUrl(typeof row.imageUrl === 'string' ? row.imageUrl : '');
    setChalkTalkImageFile(null);
  };

  const handleChalkTalkSubmit = async (e) => {
    e.preventDefault();
    const dateTrim = chalkTalkFormDate.trim();
    if (!isValidPracticeDate(dateTrim)) {
      alert('Practice date must be MM-DD-YYYY (e.g. 04-15-2026).');
      return;
    }
    if (!chalkTalkFormName.trim()) {
      alert('Drill name is required.');
      return;
    }
    const sortDate = practiceDateToSortDate(dateTrim);
    const sameDayOthers = chalkTalkDrills.filter(
      (r) => (r.practiceDate || '').trim() === dateTrim && r.id !== chalkTalkEditingId
    );
    let orderNum;
    if (chalkTalkEditingId) {
      const editingRow = chalkTalkDrills.find((r) => r.id === chalkTalkEditingId);
      orderNum = Math.max(0, Number(editingRow?.order) || 0);
    } else {
      const maxO = sameDayOthers.reduce((m, r) => Math.max(m, Number(r.order) || 0), -1);
      orderNum = maxO + 1;
    }
    setChalkTalkSaving(true);
    try {
      let imageUrl = chalkTalkFormImageUrl.trim();
      if (chalkTalkImageFile) {
        if (!chalkTalkImageFile.type.startsWith('image/')) {
          alert('Please choose an image file.');
          setChalkTalkSaving(false);
          return;
        }
        const rawExt = chalkTalkImageFile.name.split('.').pop() || 'jpg';
        const ext = /^[a-z0-9]+$/i.test(rawExt) ? rawExt.slice(0, 8).toLowerCase() : 'jpg';
        const path = `chalk-talk/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const r = storageRef(storage, path);
        await uploadBytes(r, chalkTalkImageFile, { contentType: chalkTalkImageFile.type || 'image/jpeg' });
        imageUrl = await getDownloadURL(r);
      }
      const now = Timestamp.now();
      const editingRow = chalkTalkEditingId
        ? chalkTalkDrills.find((r) => r.id === chalkTalkEditingId)
        : null;
      const payload = {
        practiceDate: dateTrim,
        sortDate,
        name: chalkTalkFormName.trim(),
        videoUrl: chalkTalkFormVideoUrl.trim(),
        notes: chalkTalkFormNotes.trim(),
        order: orderNum,
        imageUrl: imageUrl || '',
        updatedAt: now,
      };
      if (editingRow?.librarySourceId) {
        payload.librarySourceId = editingRow.librarySourceId;
      }
      if (chalkTalkEditingId) {
        await updateDoc(doc(db, 'chalkTalkDrills', chalkTalkEditingId), payload);
      } else {
        await addDoc(collection(db, 'chalkTalkDrills'), { ...payload, createdAt: now });
      }
      resetChalkTalkForm();
      await reloadChalkTalkDrills();
    } catch (err) {
      console.error(err);
      alert('Failed to save drill. Check the console and try again.');
    } finally {
      setChalkTalkSaving(false);
    }
  };

  const handleChalkTalkMoveToLibraryClick = async (row) => {
    if (
      !window.confirm(
        `Remove "${row.name || 'this drill'}" from ${row.practiceDate || 'this practice'} and save it to your drill library? You can schedule it again later from Saved drills.`
      )
    ) {
      return;
    }
    await moveScheduledDrillToLibrary(row);
  };

  const moveScheduledDrillToLibrary = async (row) => {
    const now = Timestamp.now();
    const practiceDateStr = (row.practiceDate || '').trim() || null;
    try {
      await addDoc(collection(db, 'chalkTalkDrillLibrary'), {
        name: row.name || '',
        videoUrl: row.videoUrl || row.link || '',
        notes: row.notes || '',
        imageUrl: typeof row.imageUrl === 'string' ? row.imageUrl : '',
        createdAt: now,
        updatedAt: now,
        lastUsedAt: now,
        lastUsedPracticeDate: practiceDateStr,
      });
      await deleteDoc(doc(db, 'chalkTalkDrills', row.id));
      if (chalkTalkEditingId === row.id) resetChalkTalkForm();
      await reloadChalkTalkDrills();
      await reloadChalkTalkLibrary();
    } catch (err) {
      console.error(err);
      alert('Could not move drill to saved library.');
    }
  };

  const deleteScheduledDrillPermanent = async (id) => {
    try {
      await deleteDoc(doc(db, 'chalkTalkDrills', id));
      if (chalkTalkEditingId === id) resetChalkTalkForm();
      await reloadChalkTalkDrills();
    } catch (err) {
      console.error(err);
      alert('Failed to delete.');
    }
  };

  const handleChalkTalkSaveTemplateOnly = async () => {
    if (!chalkTalkFormName.trim()) {
      alert('Drill name is required.');
      return;
    }
    setChalkTalkSaving(true);
    try {
      let imageUrl = chalkTalkFormImageUrl.trim();
      if (chalkTalkImageFile) {
        if (!chalkTalkImageFile.type.startsWith('image/')) {
          alert('Please choose an image file.');
          setChalkTalkSaving(false);
          return;
        }
        const rawExt = chalkTalkImageFile.name.split('.').pop() || 'jpg';
        const ext = /^[a-z0-9]+$/i.test(rawExt) ? rawExt.slice(0, 8).toLowerCase() : 'jpg';
        const path = `chalk-talk/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const r = storageRef(storage, path);
        await uploadBytes(r, chalkTalkImageFile, { contentType: chalkTalkImageFile.type || 'image/jpeg' });
        imageUrl = await getDownloadURL(r);
      }
      const now = Timestamp.now();
      await addDoc(collection(db, 'chalkTalkDrillLibrary'), {
        name: chalkTalkFormName.trim(),
        videoUrl: chalkTalkFormVideoUrl.trim(),
        notes: chalkTalkFormNotes.trim(),
        imageUrl: imageUrl || '',
        createdAt: now,
        updatedAt: now,
      });
      await reloadChalkTalkLibrary();
      resetChalkTalkForm();
    } catch (err) {
      console.error(err);
      alert('Could not save to library.');
    } finally {
      setChalkTalkSaving(false);
    }
  };

  const openLibraryScheduleModal = (libRow) => {
    setChalkTalkLibraryScheduleModal(libRow);
    setChalkTalkLibraryScheduleDate(htmlDateValueToPracticeDate(localSortDateToday()));
  };

  const confirmScheduleFromLibrary = async () => {
    if (!chalkTalkLibraryScheduleModal?.id) return;
    const dateTrim = chalkTalkLibraryScheduleDate.trim();
    if (!isValidPracticeDate(dateTrim)) {
      alert('Choose a valid practice date (use the calendar).');
      return;
    }
    const item = chalkTalkLibraryScheduleModal;
    const sortDate = practiceDateToSortDate(dateTrim);
    const sameDayOthers = chalkTalkDrills.filter((r) => (r.practiceDate || '').trim() === dateTrim);
    const maxO = sameDayOthers.reduce((m, r) => Math.max(m, Number(r.order) || 0), -1);
    const orderNum = maxO + 1;
    const libraryId = item.id;
    setChalkTalkSaving(true);
    try {
      const now = Timestamp.now();
      await addDoc(collection(db, 'chalkTalkDrills'), {
        practiceDate: dateTrim,
        sortDate,
        name: item.name || '',
        videoUrl: item.videoUrl || '',
        notes: item.notes || '',
        order: orderNum,
        imageUrl: typeof item.imageUrl === 'string' ? item.imageUrl : '',
        librarySourceId: libraryId,
        createdAt: now,
        updatedAt: now,
      });
      await updateDoc(doc(db, 'chalkTalkDrillLibrary', libraryId), {
        lastUsedAt: now,
        lastUsedPracticeDate: dateTrim,
        updatedAt: now,
      });
      setChalkTalkLibraryScheduleModal(null);
      setChalkTalkLibraryScheduleDate('');
      await reloadChalkTalkDrills();
      await reloadChalkTalkLibrary();
    } catch (err) {
      console.error(err);
      alert('Could not schedule drill from library.');
    } finally {
      setChalkTalkSaving(false);
    }
  };

  const deleteLibraryDrill = async (libraryId) => {
    if (!window.confirm('Remove this drill from the saved library? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'chalkTalkDrillLibrary', libraryId));
      await reloadChalkTalkLibrary();
    } catch (err) {
      console.error(err);
      alert('Failed to delete from library.');
    }
  };

  const persistChalkTalkDayOrder = useCallback(
    async (orderedIds) => {
      if (!orderedIds.length) return;
      setLoadingChalkTalkDrills(true);
      try {
        const now = Timestamp.now();
        const batch = writeBatch(db);
        orderedIds.forEach((id, index) => {
          batch.update(doc(db, 'chalkTalkDrills', id), { order: index, updatedAt: now });
        });
        await batch.commit();
        await reloadChalkTalkDrills();
      } catch (err) {
        console.error(err);
        alert('Could not save drill order. Try again.');
        setLoadingChalkTalkDrills(false);
      }
    },
    [reloadChalkTalkDrills]
  );

  const handleChalkTalkCopyPracticeToNewDate = useCallback(async () => {
    const sourceKey = (chalkTalkCopySourceKey || '').trim();
    const targetTrim = htmlDateValueToPracticeDate(chalkTalkCopyTargetHtml).trim();
    if (!sourceKey || sourceKey === '—') {
      alert('Choose which practice day to copy from.');
      return;
    }
    if (!isValidPracticeDate(targetTrim)) {
      alert('Choose a valid new practice date (use the calendar).');
      return;
    }
    if (sourceKey === targetTrim) {
      alert('Pick a different new date than the source practice.');
      return;
    }
    const sourceRows = chalkTalkDrills
      .filter((r) => (r.practiceDate || '').trim() === sourceKey)
      .slice()
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
    if (sourceRows.length === 0) {
      alert('No drills found for that practice.');
      return;
    }
    const targetSort = practiceDateToSortDate(targetTrim);
    const todaySort = localSortDateToday();
    let parentsNote = '';
    if (targetSort && targetSort < todaySort) {
      parentsNote =
        ' That new date is before today, so parents will not see these drills on Chalk Talk until you use today or a future practice date.';
    }
    if (
      !window.confirm(
        `Copy ${sourceRows.length} drill${sourceRows.length === 1 ? '' : 's'} from ${sourceKey} to ${targetTrim}?${parentsNote}`
      )
    ) {
      return;
    }
    const sameDay = chalkTalkDrills.filter((r) => (r.practiceDate || '').trim() === targetTrim);
    let nextOrder = sameDay.reduce((m, r) => Math.max(m, Number(r.order) || 0), -1) + 1;
    setChalkTalkSaving(true);
    try {
      const now = Timestamp.now();
      const batch = writeBatch(db);
      for (const row of sourceRows) {
        const newRef = doc(collection(db, 'chalkTalkDrills'));
        batch.set(newRef, {
          practiceDate: targetTrim,
          sortDate: targetSort,
          name: row.name || '',
          videoUrl: (row.videoUrl || row.link || '').trim(),
          notes: row.notes || '',
          order: nextOrder,
          imageUrl: typeof row.imageUrl === 'string' ? row.imageUrl : '',
          createdAt: now,
          updatedAt: now,
        });
        nextOrder += 1;
      }
      await batch.commit();
      await reloadChalkTalkDrills();
    } catch (err) {
      console.error(err);
      alert('Could not copy drills. Try again.');
    } finally {
      setChalkTalkSaving(false);
    }
  }, [
    chalkTalkCopySourceKey,
    chalkTalkCopyTargetHtml,
    chalkTalkDrills,
    reloadChalkTalkDrills,
  ]);

  const handleCreateDiscussion = async () => {
    const title = newDiscussionTitle.trim();
    if (!title) {
      alert('Enter a title.');
      return;
    }
    if (!newDiscussionTeamWide && newDiscussionMemberIds.length === 0) {
      alert('Select at least one parent, or mark the discussion as team-wide.');
      return;
    }
    setCreatingDiscussion(true);
    try {
      await httpsCallable(functions, 'createDiscussion')({
        title,
        isTeamWide: newDiscussionTeamWide,
        memberIds: newDiscussionTeamWide ? [] : newDiscussionMemberIds,
      });
      setNewDiscussionTitle('');
      setNewDiscussionMemberIds([]);
      alert('Discussion created.');
      await loadAdminDiscussions();
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Failed to create discussion.');
    } finally {
      setCreatingDiscussion(false);
    }
  };

  const handleDeleteTeamDiscussion = async (discussionId, title) => {
    if (!discussionId || discussionId === DEFAULT_TEAM_DISCUSSION_ID) return;
    const label = (title || '').trim() || discussionId;
    if (
      !window.confirm(
        `Delete the discussion "${label}" and all of its messages? This cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingDiscussionId(discussionId);
    try {
      await httpsCallable(functions, 'deleteDiscussionAdmin')({ discussionId });
      setAdminDiscussionsList((prev) => prev.filter((d) => d.id !== discussionId));
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Failed to delete discussion.');
    } finally {
      setDeletingDiscussionId(null);
    }
  };

  const handleEmergencyDeleteForumMessage = async () => {
    const discussionId = chatDeleteDiscussionId.trim();
    const messageId = chatDeleteMessageId.trim();
    if (!discussionId || !messageId) {
      alert('Enter discussion ID and message document ID.');
      return;
    }
    if (!window.confirm('Delete this message permanently?')) return;
    setChatDeleteBusy(true);
    try {
      await httpsCallable(functions, 'adminDeleteDiscussionMessage')({
        discussionId,
        messageId,
      });
      setChatDeleteMessageId('');
      setChatDeleteDiscussionId('');
      alert('Message deleted.');
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Failed to delete.');
    } finally {
      setChatDeleteBusy(false);
    }
  };

  const toggleParentSort = (column) => {
    setParentAccountsSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' }
    );
  };

  const parentSortIndicator = (column) =>
    parentAccountsSort.column === column ? (parentAccountsSort.direction === 'asc' ? ' ▲' : ' ▼') : '';

  const toggleLineItemsSort = (column) => {
    setLineItemsSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' }
    );
  };

  const lineItemsSortIndicator = (column) =>
    lineItemsSort.column === column ? (lineItemsSort.direction === 'asc' ? ' ▲' : ' ▼') : '';

  const toggleShowArchived = (key) => {
    setShowArchived(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRemoveMemoryLeaderboardEntry = async (entryId) => {
    if (!entryId) return;
    if (!window.confirm('Remove this entry from the Memory Game leaderboard? This cannot be undone.')) return;
    const teamId = 'glass-city-gladiators-12u';
    try {
      setRemovingEntryId(entryId);
      const docRef = doc(db, adminStatsRoot, teamId, 'memoryLeaderboard', entryId);
      await deleteDoc(docRef);
      setMemoryLeaderboard(prev => prev.filter(e => e.id !== entryId));
    } catch (err) {
      console.error('Error removing leaderboard entry:', err);
      alert('Failed to remove entry. Please try again.');
    } finally {
      setRemovingEntryId(null);
    }
  };

  const COLLECTIONS = {
    gladiatorGames: 'gladiator-games-registrations',
    tryouts: 'tryouts',
    springHockey: 'spring_hockey_registrations',
    scheduledGames: 'scheduledGames',
    submissions: 'submissions',
    uploadedDocs: 'uploaded_documents',
  };

  const handleArchive = async (sectionKey, docId) => {
    const col = COLLECTIONS[sectionKey];
    if (!col || !docId) return;
    if (!window.confirm('Archive this submission? You can restore it later from "Show archived".')) return;
    try {
      const ref = doc(db, col, docId);
      await updateDoc(ref, { archived: true, archivedAt: new Date().toISOString() });
      const updater = {
        gladiatorGames: setRegistrations,
        tryouts: setTryouts,
        springHockey: setSpringHockeyRegs,
        scheduledGames: setScheduledGames,
        submissions: setSubmissions,
        uploadedDocs: setUploadedDocs,
      }[sectionKey];
      if (updater) {
        updater(prev => {
          const next = prev.map(item => item.id === docId ? { ...item, archived: true, archivedAt: new Date().toISOString() } : item);
          if (sectionKey === 'gladiatorGames') {
            const active = next.filter(r => !r.archived);
            setRegistrationStats({
              total: active.length,
              pending: active.filter(r => r.status === 'pending').length,
              confirmed: active.filter(r => r.status === 'confirmed').length,
              paid: active.filter(r => r.status === 'paid').length
            });
          }
          return next;
        });
      }
      alert('Archived.');
    } catch (err) {
      console.error('Error archiving:', err);
      alert('Failed to archive.');
    }
  };

  const handleRestore = async (sectionKey, docId) => {
    const col = COLLECTIONS[sectionKey];
    if (!col || !docId) return;
    try {
      const ref = doc(db, col, docId);
      await updateDoc(ref, { archived: false });
      const updater = {
        gladiatorGames: setRegistrations,
        tryouts: setTryouts,
        springHockey: setSpringHockeyRegs,
        scheduledGames: setScheduledGames,
        submissions: setSubmissions,
        uploadedDocs: setUploadedDocs,
      }[sectionKey];
      if (updater) {
        updater(prev => {
          const next = prev.map(item => item.id === docId ? { ...item, archived: false, archivedAt: undefined } : item);
          if (sectionKey === 'gladiatorGames') {
            const active = next.filter(r => !r.archived);
            setRegistrationStats({
              total: active.length,
              pending: active.filter(r => r.status === 'pending').length,
              confirmed: active.filter(r => r.status === 'confirmed').length,
              paid: active.filter(r => r.status === 'paid').length
            });
          }
          return next;
        });
      }
      alert('Restored.');
    } catch (err) {
      console.error('Error restoring:', err);
      alert('Failed to restore.');
    }
  };

  const runUploadedDocsZipDownload = async (rows, filenameSuffix) => {
    if (!rows || rows.length === 0) {
      alert('No documents in this selection.');
      return;
    }
    setUploadedDocsZipBusy(true);
    setUploadedDocsZipStatus('Preparing…');
    try {
      const blob = await buildUploadedDocumentsZipBlob(storage, rows, {
        onProgress: ({ current, total }) => {
          setUploadedDocsZipStatus(`Adding files ${current} / ${total}…`);
        },
      });
      const stamp = new Date().toISOString().slice(0, 10);
      saveAs(blob, `uploaded_documents_${filenameSuffix}_${stamp}.zip`);
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Failed to build the zip file.');
    } finally {
      setUploadedDocsZipBusy(false);
      setUploadedDocsZipStatus('');
    }
  };

  const handleUploadedDocsZipAllVisible = () => {
    void runUploadedDocsZipDownload(visibleUploadedDocs, 'all');
  };

  const handleUploadedDocsZipSelectedRows = () => {
    const rows = visibleUploadedDocs.filter((r) => selectedUploadDocIds[r.id]);
    void runUploadedDocsZipDownload(rows, 'selected_rows');
  };

  const handleUploadedDocsZipSelectedParents = () => {
    void runUploadedDocsZipDownload(rowsForSelectedParentsZip, 'selected_parents');
  };

  const handleToggleUploadDocSelect = (id) => {
    setSelectedUploadDocIds((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const handleToggleAllVisibleUploadSelect = () => {
    const n = visibleUploadedDocs.length;
    if (n === 0) return;
    const allOn = selectedVisibleUploadCount === n;
    if (allOn) {
      setSelectedUploadDocIds({});
    } else {
      const next = {};
      visibleUploadedDocs.forEach((r) => {
        next[r.id] = true;
      });
      setSelectedUploadDocIds(next);
    }
  };

  const handleToggleParentForDocZip = (parentId) => {
    setSelectedParentIdsForDocZip((prev) => {
      const next = { ...prev };
      if (next[parentId]) delete next[parentId];
      else next[parentId] = true;
      return next;
    });
  };

  const handleSelectAllParentsWithUploads = () => {
    if (parentsWithUploadedForms.length === 0) return;
    const allSelected = parentsWithUploadedForms.every((p) => selectedParentIdsForDocZip[p.id]);
    if (allSelected) {
      setSelectedParentIdsForDocZip({});
    } else {
      const next = {};
      parentsWithUploadedForms.forEach((p) => {
        next[p.id] = true;
      });
      setSelectedParentIdsForDocZip(next);
    }
  };

  const handleToggleShowOnHome = async (e, row) => {
    e.stopPropagation();
    const teamId = row.teamId || DEFAULT_TEAM_STATS_TEAM_ID;
    const newVal = !(row.showOnHome !== false);
    try {
      const ref = doc(db, adminStatsRoot, teamId, 'gameHistory', row.id);
      await updateDoc(ref, { showOnHome: newVal });
      setGameHistory(prev => prev.map(r => (r.id === row.id && r.teamId === teamId) ? { ...r, showOnHome: newVal } : r));
    } catch (err) {
      console.error('Error toggling show on home:', err);
      alert('Failed to update.');
    }
  };

  /** Remove published Gazette text/photos from public pages; keeps game history stats. */
  const handleRemoveGazette = async (entry) => {
    if (!entry?.id || !entry?.teamId) return;
    const opp = entry.details?.opponent || 'this game';
    if (
      !window.confirm(
        `Pull the Gladiator Gazette for vs. ${opp} (${entry.teamLabel || getTeamStatsLabel(entry.teamId)})?\n\nThe story will disappear from Live Scoreboard, Gladiators TV, and the public Gazette page. Game stats stay in the master record.`
      )
    ) {
      return;
    }
    const key = `${entry.teamId}:${entry.id}`;
    setRemovingGazetteId(key);
    const root = adminStatsRoot;
    try {
      await updateDoc(doc(db, root, entry.teamId, 'gameHistory', entry.id), {
        recap: deleteField(),
      });
      try {
        await updateDoc(doc(db, root, entry.teamId, 'games', entry.id), {
          recap: deleteField(),
        });
      } catch (e) {
        // Game doc may already be gone or never had a recap field — history clear is enough for archive.
        console.warn('Clearing recap on live games doc (optional):', e);
      }
      setGazetteEntries((prev) => prev.filter((r) => !(r.id === entry.id && r.teamId === entry.teamId)));
    } catch (err) {
      console.error('Error removing Gazette:', err);
      alert('Failed to remove Gazette. Check staff permissions and try again.');
    } finally {
      setRemovingGazetteId(null);
    }
  };

  /** Permanently delete a game from games + gameHistory (+ fan comments / recap photos). */
  const handleDeleteGamePermanently = async (row) => {
    if (!row?.id) return;
    const teamId = row.teamId || DEFAULT_TEAM_STATS_TEAM_ID;
    const opp = row.details?.opponent || 'this game';
    const root = adminStatsRoot;
    const envLabel = root === 'stats' ? 'PRODUCTION (stats/)' : 'sandbox (statsDev/)';
    if (
      !window.confirm(
        `PERMANENTLY delete the ${row.teamLabel || getTeamStatsLabel(teamId)} game vs. ${opp} from ${envLabel}?\n\nThis removes the master record, live game doc, fan comments, and recap photos. It cannot be undone.`
      )
    ) {
      return;
    }
    if (root === 'stats' && !window.confirm('Confirm again: delete from the live website production database?')) {
      return;
    }
    const key = `${teamId}:${row.id}`;
    setDeletingGameKey(key);
    try {
      const fanSnap = await getDocs(collection(db, root, teamId, 'games', row.id, 'fanComments'));
      const batch = writeBatch(db);
      fanSnap.docs.forEach((d) => batch.delete(d.ref));
      batch.delete(doc(db, root, teamId, 'games', row.id));
      batch.delete(doc(db, root, teamId, 'gameHistory', row.id));
      await batch.commit();
      try {
        const folder = storageRef(storage, `${root}/${teamId}/recaps/${row.id}`);
        const listed = await listAll(folder);
        await Promise.all(listed.items.map((item) => deleteObject(item)));
      } catch (e) {
        console.warn('Recap photo cleanup (optional):', e);
      }
      setGameHistory((prev) => prev.filter((r) => !(r.id === row.id && r.teamId === teamId)));
      setGazetteEntries((prev) => prev.filter((r) => !(r.id === row.id && r.teamId === teamId)));
    } catch (err) {
      console.error('Error deleting game:', err);
      alert('Failed to delete game. Check staff permissions and try again.');
    } finally {
      setDeletingGameKey(null);
    }
  };

  const handleAdminStatsRootChange = (nextRoot) => {
    if (nextRoot !== 'stats' && nextRoot !== 'statsDev') return;
    if (nextRoot === adminStatsRoot) return;
    if (nextRoot === 'stats') {
      const ok = window.confirm(
        'Switch Admin stats tools to PRODUCTION (stats/)?\n\nYou will see and can delete live website games. Stat Tracker / public pages on this local server still use statsDev until you switch back.'
      );
      if (!ok) return;
    }
    setAdminStatsRoot(nextRoot);
  };

  const adminOverviewCounts = useMemo(() => {
    const sumActive = (rows) => rows.filter((r) => !r.archived).length;
    return {
      registrations:
        sumActive(registrations) +
        sumActive(tryouts) +
        sumActive(springHockeyRegs) +
        sumActive(scheduledGames) +
        sumActive(submissions) +
        sumActive(uploadedDocs),
      parents: parents.filter((p) => parentHasProfileFields(p) && parentPortalEnabled(p)).length,
      team: chalkTalkDrills.length + gladiatorsTvGames.length + gameHistory.length + memoryLeaderboard.length,
      gallery: gallery.length,
    };
  }, [
    registrations,
    tryouts,
    springHockeyRegs,
    scheduledGames,
    submissions,
    uploadedDocs,
    parents,
    chalkTalkDrills,
    gladiatorsTvGames,
    gameHistory,
    memoryLeaderboard,
    gallery,
  ]);

  const buildSectionsPayload = (fullAccess, picksSet) => {
    if (fullAccess) return ['*'];
    const arr = [...picksSet];
    if (!arr.includes(OVERVIEW_SECTION_ID)) arr.push(OVERVIEW_SECTION_ID);
    return arr;
  };

  const togglePick = (setter) => (id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreateStaffUser = async (e) => {
    e.preventDefault();
    if (!newStaffEmail.trim() || !newStaffPassword.trim()) {
      alert('Email and password are required.');
      return;
    }
    if (newStaffPassword.length < 6) {
      alert('Password must be at least 6 characters.');
      return;
    }
    setStaffFormBusy(true);
    try {
      await httpsCallable(functions, 'createStaffUser')({
        email: newStaffEmail.trim(),
        password: newStaffPassword,
        displayName: newStaffDisplayName.trim(),
        sections: buildSectionsPayload(newStaffFullAccess, newStaffSectionPicks),
      });
      setNewStaffEmail('');
      setNewStaffPassword('');
      setShowNewStaffPassword(false);
      setNewStaffDisplayName('');
      setNewStaffFullAccess(false);
      setNewStaffSectionPicks(new Set(['overview', 'gladiatorGames']));
      await loadStaffList();
      alert('Staff user created.');
    } catch (err) {
      console.error(err);
      alert(err?.message || 'Create failed.');
    } finally {
      setStaffFormBusy(false);
    }
  };

  const beginEditStaff = (u) => {
    setStaffAccountsNotice(null);
    setEditingStaffUid(u.uid);
    setEditStaffActive(u.active !== false);
    const sup = isSuperStaffFromSections(u.sections);
    setEditStaffFullAccess(sup);
    const picks = new Set();
    if (sup) {
      assignableSectionIdsForForms.forEach((id) => picks.add(id));
      picks.add(OVERVIEW_SECTION_ID);
    } else if (Array.isArray(u.sections)) {
      u.sections.forEach((x) => {
        const id = String(x);
        if (id === OVERVIEW_SECTION_ID || id === STAFF_SECTION_ID || ALL_ADMIN_SECTION_IDS.includes(id)) {
          picks.add(id);
        }
      });
    }
    setEditStaffSectionPicks(picks);
    setEditStaffPassword('');
    setShowEditStaffPassword(false);
  };

  const handleSaveEditStaff = async () => {
    if (!editingStaffUid) return;
    setStaffFormBusy(true);
    try {
      const payload = {
        uid: editingStaffUid,
        active: editStaffActive,
        sections: buildSectionsPayload(editStaffFullAccess, editStaffSectionPicks),
      };
      if (editStaffPassword.trim()) payload.password = editStaffPassword.trim();
      await httpsCallable(functions, 'updateStaffUser')(payload);
      setEditingStaffUid(null);
      await loadStaffList();
      setStaffAccountsNotice({ variant: 'success', message: 'Staff account updated.' });
    } catch (err) {
      console.error(err);
      setStaffAccountsNotice({ variant: 'error', message: err?.message || 'Update failed.' });
    } finally {
      setStaffFormBusy(false);
    }
  };

  const handleRemoveStaff = async () => {
    if (!editingStaffUid) return;
    if (auth.currentUser?.uid === editingStaffUid) {
      setStaffAccountsNotice({
        variant: 'error',
        message: 'You cannot remove your own staff account.',
      });
      return;
    }
    const row = staffList.find((u) => u.uid === editingStaffUid);
    const label = (row?.email || row?.displayName || editingStaffUid).trim();
    if (
      !window.confirm(
        `Remove staff access for ${label}? Their portal login will be deleted. This cannot be undone.`,
      )
    ) {
      return;
    }
    setStaffFormBusy(true);
    try {
      await httpsCallable(functions, 'deleteStaffUser')({ uid: editingStaffUid });
      setEditingStaffUid(null);
      await loadStaffList();
      setStaffAccountsNotice({ variant: 'success', message: 'Staff member removed.' });
    } catch (err) {
      console.error(err);
      setStaffAccountsNotice({ variant: 'error', message: err?.message || 'Remove failed.' });
    } finally {
      setStaffFormBusy(false);
    }
  };

  if (adminGate === 'loading') {
    return (
      <div className="admin-portal admin-portal-login">
        <h2 className="admin-heading">Admin Portal</h2>
        <p style={{ color: '#999' }}>Loading…</p>
      </div>
    );
  }

  if (adminGate !== 'ok') {
    const onSubmitLogin = async (ev) => {
      ev.preventDefault();
      setLoginError('');
      try {
        await signInWithEmailAndPassword(auth, adminEmail.trim(), adminPassword);
        setAdminPassword('');
      } catch (err) {
        setLoginError('Sign-in failed. Check email and password.');
      }
    };
    return (
      <div className="admin-portal admin-portal-login">
        <h2 className="admin-heading">Admin sign in</h2>
        <p style={{ color: '#888', fontSize: '0.9rem', maxWidth: '360px', marginBottom: '16px' }}>
          Use the staff email and password from your team. Access is controlled in Firestore (collection{' '}
          <code style={{ color: '#aaa' }}>adminUsers</code>).
        </p>
        {authNotice ? (
          <p className="admin-login-error" role="alert">
            {authNotice}
          </p>
        ) : null}
        <form onSubmit={onSubmitLogin}>
          <input
            type="email"
            placeholder="Staff email"
            value={adminEmail}
            onChange={(e) => {
              setAdminEmail(e.target.value);
              setLoginError('');
            }}
            autoComplete="username"
            style={{ display: 'block', width: '100%', maxWidth: '320px', marginBottom: '10px', padding: '10px' }}
          />
          <input
            type="password"
            placeholder="Password"
            value={adminPassword}
            onChange={(e) => {
              setAdminPassword(e.target.value);
              setLoginError('');
            }}
            autoComplete="current-password"
            style={{ display: 'block', width: '100%', maxWidth: '320px', marginBottom: '10px', padding: '10px' }}
          />
          {loginError ? (
            <p className="admin-login-error" role="alert">
              {loginError}
            </p>
          ) : null}
          <button type="submit" className="admin-login-btn">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-portal admin-portal-authenticated">
      <header className="admin-shell-header">
        <button
          type="button"
          className="admin-nav-toggle"
          aria-label={adminNavOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={adminNavOpen}
          onClick={() => setAdminNavOpen((o) => !o)}
        />
        <h1 className="admin-heading admin-shell-heading">Admin Portal</h1>
        <button
          type="button"
          className="admin-login-btn"
          style={{ marginLeft: 'auto' }}
          onClick={() => signOut(auth)}
        >
          Sign out
        </button>
      </header>

      <div className={`admin-shell ${adminNavOpen ? 'admin-shell-nav-open' : ''}`}>
        <aside className="admin-nav" aria-label="Admin menu">
          <button
            type="button"
            className={`admin-nav-item ${activeAdminSection === 'overview' ? 'is-active' : ''}`}
            onClick={() => {
              setActiveSection('overview');
              setAdminNavOpen(false);
            }}
          >
            Overview
          </button>
          {filteredNavGroups.map((g) => (
            <div key={g.groupId} className="admin-nav-group">
              <div className="admin-nav-group-label">{g.groupLabel}</div>
              {g.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`admin-nav-item ${activeAdminSection === item.id ? 'is-active' : ''}`}
                  onClick={() => {
                    setActiveSection(item.id);
                    setAdminNavOpen(false);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </aside>
        <div
          className="admin-nav-scrim"
          role="presentation"
          onClick={() => setAdminNavOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setAdminNavOpen(false)}
        />
        <main className="admin-main">
          {activeAdminSection === 'overview' && allowedSectionSet.has(OVERVIEW_SECTION_ID) && (
            <div className="admin-overview">
              <h2 className="admin-main-section-title">Overview</h2>
              <p className="admin-overview-lead">
                Pick a workspace from the left, or open a category below.
              </p>
              <div className="admin-overview-cards">
                {filteredNavGroups.map((g) => (
                  <button
                    key={g.groupId}
                    type="button"
                    className="admin-overview-card"
                    onClick={() => setActiveSection(g.items[0].id)}
                  >
                    <span className="admin-overview-card-title">{g.groupLabel}</span>
                    <span className="admin-overview-card-meta">
                      {g.groupId === 'registrations' && `${adminOverviewCounts.registrations} active rows`}
                      {g.groupId === 'parents' && `${adminOverviewCounts.parents} families`}
                      {g.groupId === 'team' && `${adminOverviewCounts.team} items`}
                      {g.groupId === 'media' && `${adminOverviewCounts.gallery} photos`}
                      {g.groupId === 'staff' && 'Super-admin'}
                    </span>
                    <span className="admin-overview-card-action">Open</span>
                  </button>
                ))}
              </div>
            </div>
          )}

      {activeAdminSection === 'parentChat' && allowedSectionSet.has('parentChat') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">Parent team chat</h2>
          <div style={{ padding: '0 0 16px' }}>
            <p style={{ color: '#999', marginBottom: '12px', maxWidth: '720px' }}>
              Parents see discussions under Parent Portal → Team Chat → Discussions. Manage discussions below, or delete a
              single message in an emergency. Backend actions require your signed-in staff account (Team chat permission).
            </p>

            <h3 style={{ color: '#ccc', fontSize: '1rem', margin: '20px 0 8px' }}>Team discussions</h3>
            <p style={{ color: '#999', fontSize: '0.9rem', marginBottom: '10px', maxWidth: '720px' }}>
              The default <code className="admin-discussions-inline-code">general</code> discussion cannot be removed. Deleting a
              discussion removes all messages in that thread.
            </p>
            <div className="admin-discussions-toolbar">
              <button
                type="button"
                className="admin-button"
                onClick={() => loadAdminDiscussions()}
                disabled={adminDiscussionsLoading}
              >
                {adminDiscussionsLoading ? 'Loading…' : 'Refresh list'}
              </button>
            </div>
            {adminDiscussionsLoading && adminDiscussionsList.length === 0 ? (
              <p style={{ color: '#888' }}>Loading discussions…</p>
            ) : adminDiscussionsList.length === 0 ? (
              <p style={{ color: '#888' }}>No discussions found.</p>
            ) : (
              <div className="admin-discussions-table-wrap">
                <table className="admin-discussions-table">
                  <thead>
                    <tr>
                      <th scope="col">Title</th>
                      <th scope="col">ID</th>
                      <th scope="col">Scope</th>
                      <th scope="col">Created</th>
                      <th scope="col" className="admin-discussions-table__actions">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminDiscussionsList.map((d) => {
                      const id = d.id || '';
                      const isDefault = id === DEFAULT_TEAM_DISCUSSION_ID;
                      const title = (d.title || '').trim() || '(untitled)';
                      return (
                        <tr key={id}>
                          <td>{title}</td>
                          <td>
                            <code className="admin-discussions-id">{id}</code>
                          </td>
                          <td>{d.isTeamWide ? 'Team-wide' : 'Members only'}</td>
                          <td>{formatDiscussionTimestamp(d.createdAt)}</td>
                          <td className="admin-discussions-table__actions">
                            <button
                              type="button"
                              className="admin-button admin-button-danger"
                              onClick={() => handleDeleteTeamDiscussion(id, title)}
                              disabled={isDefault || deletingDiscussionId === id}
                              title={
                                isDefault
                                  ? 'The default General Team Chat cannot be deleted.'
                                  : 'Delete this discussion and all messages'
                              }
                            >
                              {deletingDiscussionId === id ? 'Deleting…' : 'Delete'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <h3 style={{ color: '#ccc', fontSize: '1rem', margin: '20px 0 8px' }}>Create discussion</h3>
            <div className="admin-discussion-title-row">
              <input
                type="text"
                value={newDiscussionTitle}
                onChange={(e) => setNewDiscussionTitle(e.target.value)}
                placeholder="Title (e.g. Spring tournament)"
                className="admin-discussion-title-input"
              />
              <label className="admin-discussion-teamwide">
                <input
                  type="checkbox"
                  checked={newDiscussionTeamWide}
                  onChange={(e) => {
                    setNewDiscussionTeamWide(e.target.checked);
                    if (e.target.checked) setNewDiscussionMemberIds([]);
                  }}
                />
                Team-wide (all parents)
              </label>
            </div>
            {!newDiscussionTeamWide && (
              <>
                <p className="admin-parent-picker-hint">
                  Only active parents with portal access are listed. Search to narrow the list.
                </p>
                <input
                  type="search"
                  className="admin-parent-picker-search"
                  placeholder="Search by name or email…"
                  value={newDiscussionParentSearch}
                  onChange={(e) => setNewDiscussionParentSearch(e.target.value)}
                  aria-label="Search parents for new discussion"
                />
                <div className="admin-parent-picker">
                  {filteredNewDiscussionParents.length === 0 ? (
                    <p className="admin-parent-picker-empty">No parents match your search.</p>
                  ) : (
                    filteredNewDiscussionParents.map((p) => (
                      <label key={p.id} className="admin-parent-picker-row">
                        <input
                          type="checkbox"
                          className="admin-parent-picker-checkbox"
                          checked={newDiscussionMemberIds.includes(p.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewDiscussionMemberIds((prev) => [...prev, p.id]);
                            } else {
                              setNewDiscussionMemberIds((prev) => prev.filter((x) => x !== p.id));
                            }
                          }}
                        />
                        <span className="admin-parent-picker-name">
                          {p.name || p.displayName || p.id}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </>
            )}
            <button
              type="button"
              className="admin-button"
              onClick={handleCreateDiscussion}
              disabled={creatingDiscussion}
            >
              {creatingDiscussion ? 'Creating…' : 'Create discussion'}
            </button>

            <h3 style={{ color: '#ccc', fontSize: '1rem', margin: '20px 0 8px' }}>Emergency: delete a message</h3>
            <p style={{ color: '#999', fontSize: '0.9rem', marginBottom: '8px' }}>
              Discussion ID and message document ID (from Firebase console if needed).
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
              <input
                type="text"
                value={chatDeleteDiscussionId}
                onChange={(e) => setChatDeleteDiscussionId(e.target.value)}
                placeholder="Discussion ID"
                style={{ padding: '8px', minWidth: '220px' }}
              />
              <input
                type="text"
                value={chatDeleteMessageId}
                onChange={(e) => setChatDeleteMessageId(e.target.value)}
                placeholder="Message document ID"
                style={{ padding: '8px', minWidth: '220px' }}
              />
              <button
                type="button"
                className="admin-button"
                onClick={handleEmergencyDeleteForumMessage}
                disabled={chatDeleteBusy}
              >
                {chatDeleteBusy ? 'Deleting…' : 'Delete message'}
              </button>
            </div>
          </div>
      </div>
      )}

      {/* GLADIATOR GAMES REGISTRATIONS SECTION */}
      {activeAdminSection === 'gladiatorGames' && allowedSectionSet.has('gladiatorGames') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          🏒 Gladiator Games Registrations
          {registrations.filter(r => !r.archived).length > 0 && (
            <span className="admin-section-count"> ({registrations.filter(r => !r.archived).length})</span>
          )}
        </h2>
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#999' }}>
                <input type="checkbox" checked={showArchived.gladiatorGames} onChange={() => toggleShowArchived('gladiatorGames')} />
                Show archived
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '20px' }}>
              <div style={{ background: '#222', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '2px solid #00a7a9' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00a7a9' }}>{registrationStats.total}</div>
                <div style={{ color: '#999', fontSize: '0.9rem' }}>Total</div>
              </div>
              <div style={{ background: '#222', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '2px solid #FFC107' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#FFC107' }}>{registrationStats.pending}</div>
                <div style={{ color: '#999', fontSize: '0.9rem' }}>Pending</div>
              </div>
              <div style={{ background: '#222', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '2px solid #4CAF50' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4CAF50' }}>{registrationStats.confirmed}</div>
                <div style={{ color: '#999', fontSize: '0.9rem' }}>Confirmed</div>
              </div>
              <div style={{ background: '#222', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '2px solid #B4975A' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#B4975A' }}>{registrationStats.paid}</div>
                <div style={{ color: '#999', fontSize: '0.9rem' }}>Paid</div>
              </div>
            </div>
            <button onClick={handleExportRegistrations} className="admin-button" style={{ marginBottom: '15px' }}>
              📤 Export Registrations to CSV
            </button>
            {loadingRegistrations ? (
              <p>Loading registrations...</p>
            ) : (showArchived.gladiatorGames ? registrations : registrations.filter(r => !r.archived)).length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '40px 0' }}>
                {showArchived.gladiatorGames ? 'No registrations (including archived).' : 'No registrations yet. Share the registration link!'}
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Submitted</th>
                      <th>Parent Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Skater Name</th>
                      <th>Birth Year</th>
                      <th>Position</th>
                      <th>Experience</th>
                      <th>Future Interest</th>
                      <th>Add'l Tickets</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showArchived.gladiatorGames ? registrations : registrations.filter(r => !r.archived)).map(reg => (
                      <tr key={reg.id} className={reg.archived ? 'admin-row-archived' : ''}>
                        <td>{new Date(reg.submittedAt).toLocaleDateString()}</td>
                        <td>{reg.parentFirstName} {reg.parentLastName}</td>
                        <td>
                          <a href={`mailto:${reg.email}`} style={{ color: '#00a7a9' }}>
                            {reg.email}
                          </a>
                        </td>
                        <td>
                          <a href={`tel:${reg.phone}`} style={{ color: '#00a7a9' }}>
                            {reg.phone}
                          </a>
                        </td>
                        <td style={{ fontWeight: 'bold' }}>{reg.skaterName}</td>
                        <td>{reg.birthYear}</td>
                        <td>{reg.position}</td>
                        <td style={{ fontSize: '0.85rem' }}>{reg.yearsOfExperience || 'N/A'}</td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {reg.futureEvents === 'Yes, both Spring and Fall' ? '🌟 Both' : 
                           reg.futureEvents === 'Yes, keep me informed about Spring events' ? 'Spring' :
                           reg.futureEvents === 'Yes, keep me informed about Fall events' ? 'Fall' : 
                           'Just event'}
                        </td>
                        <td>
                          {reg.wantsAdditionalTickets === 'Yes' ? (
                            <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>
                              Yes ({reg.additionalTicketsCount || 0})
                            </span>
                          ) : (
                            <span style={{ color: '#999' }}>No</span>
                          )}
                        </td>
                        <td>
                          <select
                            value={reg.status}
                            onChange={(e) => handleUpdateRegistrationStatus(reg.id, e.target.value)}
                            style={{
                              background: '#222',
                              color: 'white',
                              border: '1px solid #555',
                              padding: '5px',
                              borderRadius: '4px'
                            }}
                            disabled={!!reg.archived}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="paid">Paid</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {reg.archived ? (
                            <button type="button" className="admin-button-restore" onClick={() => handleRestore('gladiatorGames', reg.id)}>Restore</button>
                          ) : (
                            <button type="button" className="admin-button-archive" onClick={() => handleArchive('gladiatorGames', reg.id)}>Archive</button>
                          )}
                          {!reg.archived && (
                            <button
                              onClick={() => handleDeleteRegistration(reg.id)}
                              style={{
                                background: '#f44336',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                marginLeft: '6px'
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
      </div>
      )}

      {/* TRYOUTS */}
      {activeAdminSection === 'tryouts' && allowedSectionSet.has('tryouts') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          🏑 Tryouts Registrations
          {tryouts.filter(r => !r.archived).length > 0 && (
            <span className="admin-section-count"> ({tryouts.filter(r => !r.archived).length})</span>
          )}
        </h2>
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#999' }}>
                <input type="checkbox" checked={showArchived.tryouts} onChange={() => toggleShowArchived('tryouts')} />
                Show archived
              </label>
              <button onClick={handleExportTryouts} className="admin-button" style={{ marginLeft: 'auto' }}>
                📤 Export Tryouts to CSV
              </button>
            </div>
            {loadingTryouts ? (
              <p>Loading tryouts...</p>
            ) : (showArchived.tryouts ? tryouts : tryouts.filter(r => !r.archived)).length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                {showArchived.tryouts ? 'No tryout registrations (including archived).' : 'No tryout registrations yet.'}
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Submitted</th>
                      <th>Participant</th>
                      <th>Parent</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Birth Yr</th>
                      <th>Position</th>
                      <th>Experience</th>
                      <th>Current Team</th>
                      <th>Looking For</th>
                      <th>Fee</th>
                      <th>Heard From</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showArchived.tryouts ? tryouts : tryouts.filter(r => !r.archived)).map(row => (
                      <tr key={row.id} className={row.archived ? 'admin-row-archived' : ''}>
                        <td>{row.submittedAt ? (typeof row.submittedAt?.toDate === 'function' ? row.submittedAt.toDate().toLocaleDateString() : new Date(row.submittedAt).toLocaleDateString()) : '—'}</td>
                        <td>{row.participantName || row.skaterName || '—'}</td>
                        <td>{row.parentName || (row.parentFirstName && row.parentLastName ? `${row.parentFirstName} ${row.parentLastName}` : null) || '—'}</td>
                        <td><a href={`mailto:${row.email}`} style={{ color: '#00a7a9' }}>{row.email || '—'}</a></td>
                        <td><a href={`tel:${row.phone}`} style={{ color: '#00a7a9' }}>{row.phone || '—'}</a></td>
                        <td>{row.birthYear || '—'}</td>
                        <td>{row.position || '—'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{row.yearsOfExperience || row.yearsPlayed || row.experience || '—'}</td>
                        <td style={{ fontSize: '0.85rem', maxWidth: '120px' }}>{row.currentTeam || '—'}</td>
                        <td style={{ fontSize: '0.85rem', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.lookingForFromGladiators || ''}>{row.lookingForFromGladiators || '—'}</td>
                        <td>{row.fee ? `$${row.fee}` : '—'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{row.howDidYouHear || row.heardFrom || row.otherSource || '—'}</td>
                        <td>
                          {row.archived ? (
                            <button type="button" className="admin-button-restore" onClick={() => handleRestore('tryouts', row.id)}>Restore</button>
                          ) : (
                            <button type="button" className="admin-button-archive" onClick={() => handleArchive('tryouts', row.id)}>Archive</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
      </div>
      )}

      {/* SPRING HOCKEY (parent portal) */}
      {activeAdminSection === 'springHockey' && allowedSectionSet.has('springHockey') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          🌸 Spring Hockey — parent form submissions
          {springHockeyRegs.filter(r => !r.archived).length > 0 && (
            <span className="admin-section-count"> ({springHockeyRegs.filter(r => !r.archived).length})</span>
          )}
        </h2>
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#999' }}>
                <input type="checkbox" checked={showArchived.springHockey} onChange={() => toggleShowArchived('springHockey')} />
                Show archived
              </label>
              <button type="button" onClick={handleExportSpringHockey} className="admin-button" style={{ marginLeft: 'auto' }}>
                📤 Export Spring Hockey to CSV
              </button>
            </div>
            {loadingSpringHockey ? (
              <p>Loading Spring Hockey submissions…</p>
            ) : (showArchived.springHockey ? springHockeyRegs : springHockeyRegs.filter(r => !r.archived)).length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                {showArchived.springHockey ? 'No submissions (including archived).' : 'No Spring Hockey submissions yet.'}
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Submitted</th>
                      <th>Player</th>
                      <th>Parent</th>
                      <th>Email</th>
                      <th>Pos</th>
                      <th>#</th>
                      <th>Sizes</th>
                      <th>Warmup / pants</th>
                      <th>Gloves</th>
                      <th>Photo</th>
                      <th>Bio / fun fact</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showArchived.springHockey ? springHockeyRegs : springHockeyRegs.filter(r => !r.archived)).map(row => (
                      <tr key={row.id} className={row.archived ? 'admin-row-archived' : ''}>
                        <td>
                          {row.submittedAt
                            ? (typeof row.submittedAt?.toDate === 'function'
                                ? row.submittedAt.toDate().toLocaleDateString()
                                : new Date(row.submittedAt).toLocaleDateString())
                            : '—'}
                        </td>
                        <td>{row.playerName || '—'}</td>
                        <td>{row.parentDisplayName || '—'}</td>
                        <td>
                          <a href={`mailto:${row.parentEmail}`} style={{ color: '#00a7a9' }}>{row.parentEmail || '—'}</a>
                        </td>
                        <td>{row.position || '—'}</td>
                        <td>
                          {row.desiredNumber || '—'}
                          {row.backupDesiredNumber ? ` / ${row.backupDesiredNumber}` : ''}
                        </td>
                        <td style={{ fontSize: '0.8rem', maxWidth: '160px', whiteSpace: 'normal' }}>
                          J {row.jerseySize || '—'} · T {row.tShirtSize || '—'} · Sw {row.sweatshirtSize || '—'}
                        </td>
                        <td style={{ fontSize: '0.8rem', maxWidth: '160px', whiteSpace: 'normal' }}>
                          <div>
                            W:{' '}
                            {row.warmupJacketPick
                              ? row.warmupJacketPick
                                  .replace(/^youth\|/i, 'Youth ')
                                  .replace(/^men\|/i, 'Men ')
                              : `${row.warmupJacketFit || '—'} ${row.warmupJacketSize || ''}`.trim()}{' '}
                            ({row.warmupJacketType || '—'})
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            Pant: {(row.pantsFit || '—') + ' ' + (row.pantsSize || '—')}
                          </div>
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {row.gloveCategory && row.gloveSizeInches
                            ? `${row.gloveCategory} ${row.gloveSizeInches}"`
                            : (row.hockeyGloveSize || '—')}
                        </td>
                        <td>
                          {row.rosterPhotoUrl ? (
                            <span>
                              <a href={row.rosterPhotoUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#00a7a9' }}>View</a>
                              {row.rosterPhotoPermissionAcknowledged ? (
                                <span style={{ display: 'block', fontSize: '0.75rem', color: '#8c8', marginTop: '4px' }}>OK use</span>
                              ) : null}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ fontSize: '0.8rem', maxWidth: '200px', whiteSpace: 'normal' }}>
                          <div title={row.bio || ''}>{(row.bio || '').slice(0, 80)}{(row.bio || '').length > 80 ? '…' : ''}</div>
                          <div style={{ marginTop: '4px', fontStyle: 'italic' }} title={row.funFact || ''}>
                            {(row.funFact || '').slice(0, 60)}{(row.funFact || '').length > 60 ? '…' : ''}
                          </div>
                        </td>
                        <td>
                          {row.archived ? (
                            <button type="button" className="admin-button-restore" onClick={() => handleRestore('springHockey', row.id)}>Restore</button>
                          ) : (
                            <button type="button" className="admin-button-archive" onClick={() => handleArchive('springHockey', row.id)}>Archive</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
      </div>
      )}

      {activeAdminSection === 'teamSchedule' && allowedSectionSet.has('teamSchedule') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">Team Schedule</h2>
        <p style={{ color: '#999', marginBottom: '14px', maxWidth: '860px' }}>
          Team schedule feeds are the source of truth. Update events in TeamSnap and this preview updates automatically for parents. The controls below match what parents see (12U/14U, event types, and view modes).
        </p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
          {teamScheduleFeedUrls.map((url, idx) => (
            <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="admin-button">
              Open calendar feed {idx + 1}
            </a>
          ))}
          <button type="button" className="admin-button" onClick={refreshUpcomingScheduleEvents}>
            Refresh preview
          </button>
        </div>
        <p style={{ color: '#9a9a9a', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Last loaded:{' '}
          {upcomingScheduleLastLoadedAt
            ? upcomingScheduleLastLoadedAt.toLocaleString()
            : 'Not loaded yet'}
        </p>
        <TeamScheduleHighlights
          events={upcomingScheduleEvents}
          loading={loadingUpcomingScheduleEvents}
          error={upcomingScheduleEventsError}
          onRetry={refreshUpcomingScheduleEvents}
          compact
        />
      </div>
      )}

      {activeAdminSection === 'teamRoster' && allowedSectionSet.has('teamRoster') && (
        <div className="admin-section">
          <RosterAdmin />
        </div>
      )}

      {activeAdminSection === 'teamCoaches' && allowedSectionSet.has('teamCoaches') && (
        <div className="admin-section">
          <CoachesAdmin />
        </div>
      )}

      {activeAdminSection === 'sponsors' && allowedSectionSet.has('sponsors') && (
        <div className="admin-section">
          <SponsorsAdmin />
        </div>
      )}

      {/* CHALK TALK / PRACTICE DRILLS */}
      {activeAdminSection === 'chalkTalk' && allowedSectionSet.has('chalkTalk') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          🏒 Chalk Talk (practice drills)
          {chalkTalkDrills.length > 0 && (
            <span className="admin-section-count"> ({chalkTalkDrills.length})</span>
          )}
        </h2>
          <>
            <p style={{ color: '#999', marginBottom: '16px', maxWidth: '720px' }}>
              Parents only see drills for <strong>today and future</strong> practice dates on Chalk Talk (past days stay in this list for your records). Pick a practice date with the calendar (MM-DD-YYYY). Drag the handle to reorder that day. Use <strong>Copy practice to a new date</strong> to repeat last week’s plan, or use <strong>Saved drills</strong> below for templates, scheduling on any date, or moving a drill off the schedule without losing it.
            </p>
            {chalkTalkDrills.length > 0 && (
              <div className="chalk-admin-copy-panel">
                <h3 className="chalk-admin-subheading chalk-admin-copy-panel__title">Copy practice to a new date</h3>
                <p className="chalk-admin-copy-panel__hint">
                  Duplicate every drill from one practice onto another day (same videos, notes, and images). New copies are appended after any drills already on the target date.
                </p>
                <div className="chalk-admin-copy-panel__row">
                  <label className="chalk-admin-copy-panel__label">
                    From
                    <select
                      value={chalkTalkCopySourceKey}
                      onChange={(e) => setChalkTalkCopySourceKey(e.target.value)}
                      className="admin-input"
                    >
                      <option value="">Select practice…</option>
                      {chalkTalkDrillsGrouped
                        .filter((g) => g.practiceDateKey && g.practiceDateKey !== '—')
                        .map((g) => (
                          <option key={g.practiceDateKey} value={g.practiceDateKey}>
                            {g.practiceDateKey} ({g.rows.length} drill{g.rows.length === 1 ? '' : 's'})
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="chalk-admin-copy-panel__label">
                    New practice date
                    <input
                      type="date"
                      value={chalkTalkCopyTargetHtml}
                      onChange={(e) => setChalkTalkCopyTargetHtml(e.target.value)}
                      className="admin-input chalk-admin-copy-panel__date"
                    />
                  </label>
                  <button
                    type="button"
                    className="admin-button"
                    disabled={chalkTalkSaving || !chalkTalkCopySourceKey}
                    onClick={handleChalkTalkCopyPracticeToNewDate}
                  >
                    {chalkTalkSaving ? 'Copying…' : 'Copy all drills'}
                  </button>
                </div>
              </div>
            )}
            <form
              onSubmit={handleChalkTalkSubmit}
              style={{
                marginBottom: '24px',
                padding: '16px',
                background: 'rgba(0, 167, 169, 0.06)',
                borderRadius: '8px',
                border: '1px solid #2a3444',
                maxWidth: '640px',
              }}
            >
              <h3 style={{ margin: '0 0 12px', fontSize: '1rem', color: '#e6e8ec' }}>
                {chalkTalkEditingId ? 'Edit drill' : 'Add drill'}
              </h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#ccc' }}>
                  Practice date
                  <input
                    type="date"
                    value={practiceDateToHtmlDateValue(chalkTalkFormDate)}
                    onChange={(e) => {
                      const p = htmlDateValueToPracticeDate(e.target.value);
                      setChalkTalkFormDate(p);
                    }}
                    className="admin-input"
                    style={{ maxWidth: '200px' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#ccc' }}>
                  Drill name
                  <input
                    type="text"
                    value={chalkTalkFormName}
                    onChange={(e) => setChalkTalkFormName(e.target.value)}
                    className="admin-input"
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#ccc' }}>
                  Video URL (optional)
                  <input
                    type="url"
                    value={chalkTalkFormVideoUrl}
                    onChange={(e) => setChalkTalkFormVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=…"
                    className="admin-input"
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#ccc' }}>
                  Notes (Markdown: **bold**, - bullets)
                  <textarea
                    value={chalkTalkFormNotes}
                    onChange={(e) => setChalkTalkFormNotes(e.target.value)}
                    rows={3}
                    className="admin-input"
                  />
                </label>
                {chalkTalkFormNotes.trim() ? (
                  <div
                    style={{
                      padding: '10px 12px',
                      background: 'rgba(0,0,0,0.25)',
                      borderRadius: '6px',
                      border: '1px solid #2a3444',
                      fontSize: '0.9rem',
                      color: '#e6e8ec',
                    }}
                  >
                    <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '6px' }}>Preview</div>
                    <ChalkTalkDrillNotes markdown={chalkTalkFormNotes} className="drill-notes drill-notes--admin-preview" />
                  </div>
                ) : null}
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#ccc' }}>
                  Drill photo (optional)
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      setChalkTalkImageFile(f || null);
                    }}
                  />
                </label>
                {chalkTalkFormImageUrl && !chalkTalkImageFile && (
                  <div style={{ fontSize: '0.85rem', color: '#8a9' }}>
                    Current image:{' '}
                    <a href={chalkTalkFormImageUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#00a7a9' }}>
                      view
                    </a>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
                <button type="submit" className="admin-button" disabled={chalkTalkSaving}>
                  {chalkTalkSaving ? 'Saving…' : chalkTalkEditingId ? 'Update drill' : 'Add drill'}
                </button>
                {!chalkTalkEditingId ? (
                  <button
                    type="button"
                    className="admin-button"
                    disabled={chalkTalkSaving}
                    onClick={handleChalkTalkSaveTemplateOnly}
                    title="Save as a reusable template without putting it on the schedule"
                  >
                    Save to library only
                  </button>
                ) : null}
                {chalkTalkEditingId && (
                  <button type="button" className="admin-button" onClick={resetChalkTalkForm}>
                    Cancel edit
                  </button>
                )}
              </div>
            </form>
            <h3 className="chalk-admin-subheading">Scheduled drills</h3>
            {loadingChalkTalkDrills ? (
              <p>Loading drills…</p>
            ) : chalkTalkDrills.length === 0 ? (
              <p style={{ color: '#999', marginBottom: '1.5rem' }}>No drills on the schedule yet. Add one above or schedule from saved drills.</p>
            ) : (
              <ChalkTalkDrillsAdminList
                groups={chalkTalkDrillsGrouped}
                onReorderDay={persistChalkTalkDayOrder}
                disabled={loadingChalkTalkDrills}
                chalkTalkDrillAckers={chalkTalkDrillAckers}
                chalkTalkAckersLoading={chalkTalkAckersLoading}
                chalkTalkExpandedAckDrillId={chalkTalkExpandedAckDrillId}
                setChalkTalkExpandedAckDrillId={setChalkTalkExpandedAckDrillId}
                parents={parents}
                onEdit={startEditChalkTalkDrill}
                onMoveToLibrary={handleChalkTalkMoveToLibraryClick}
                onRequestRemove={setChalkTalkRemoveTarget}
              />
            )}

            <h3 className="chalk-admin-subheading" style={{ marginTop: '2rem' }}>
              Saved drills (library)
            </h3>
            <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '12px', maxWidth: '720px' }}>
              Reusable templates. <strong>Last used</strong> updates when you schedule this drill on a practice. Removing a drill from the schedule can move it here with that date recorded.
            </p>
            {loadingChalkTalkLibrary ? (
              <p>Loading saved drills…</p>
            ) : chalkTalkLibrary.length === 0 ? (
              <p style={{ color: '#999' }}>No saved drills yet. Use &quot;Save to library only&quot; or move a scheduled drill into the library when you remove it.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table" style={{ minWidth: '720px' }}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Notes</th>
                      <th>Last used</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chalkTalkLibrary.map((lib) => {
                      const lastUsedLabel = (() => {
                        const pd = lib.lastUsedPracticeDate;
                        const at = lib.lastUsedAt;
                        if (pd && at && typeof at.toDate === 'function') {
                          return `${pd} (${at.toDate().toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })})`;
                        }
                        if (pd) return String(pd);
                        if (at && typeof at.toDate === 'function') {
                          return at.toDate().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
                        }
                        return 'Never';
                      })();
                      return (
                        <tr key={lib.id}>
                          <td>{lib.name || '—'}</td>
                          <td style={{ fontSize: '0.8rem', maxWidth: '240px', whiteSpace: 'normal' }}>
                            {(lib.notes || '').slice(0, 100)}
                            {(lib.notes || '').length > 100 ? '…' : ''}
                          </td>
                          <td style={{ fontSize: '0.85rem', color: '#aaa' }}>{lastUsedLabel}</td>
                          <td>
                            <button
                              type="button"
                              className="admin-button"
                              disabled={chalkTalkSaving}
                              onClick={() => openLibraryScheduleModal(lib)}
                            >
                              Schedule…
                            </button>{' '}
                            <button
                              type="button"
                              className="admin-button-archive"
                              onClick={() => deleteLibraryDrill(lib.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {chalkTalkRemoveTarget ? (
              <div
                className="chalk-admin-modal-scrim"
                role="presentation"
                onClick={() => setChalkTalkRemoveTarget(null)}
              >
                <div
                  className="chalk-admin-modal"
                  role="dialog"
                  aria-labelledby="chalk-remove-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h4 id="chalk-remove-title" style={{ marginTop: 0, color: '#e6e8ec' }}>
                    Delete from schedule?
                  </h4>
                  <p style={{ color: '#aaa', fontSize: '0.95rem', lineHeight: 1.5 }}>
                    Permanently delete <strong>{chalkTalkRemoveTarget.name || 'this drill'}</strong> (
                    {chalkTalkRemoveTarget.practiceDate || '—'})? Parents will no longer see it, and it will{' '}
                    <strong>not</strong> be saved to your library. To keep a copy, use <strong>Move to library</strong> on the
                    row instead.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '16px' }}>
                    <button
                      type="button"
                      className="admin-button-archive"
                      onClick={async () => {
                        const id = chalkTalkRemoveTarget.id;
                        setChalkTalkRemoveTarget(null);
                        await deleteScheduledDrillPermanent(id);
                      }}
                    >
                      Delete permanently
                    </button>
                    <button type="button" className="admin-button" onClick={() => setChalkTalkRemoveTarget(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {chalkTalkLibraryScheduleModal ? (
              <div
                className="chalk-admin-modal-scrim"
                role="presentation"
                onClick={() => {
                  setChalkTalkLibraryScheduleModal(null);
                  setChalkTalkLibraryScheduleDate('');
                }}
              >
                <div
                  className="chalk-admin-modal"
                  role="dialog"
                  aria-labelledby="chalk-lib-sched-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h4 id="chalk-lib-sched-title" style={{ marginTop: 0, color: '#e6e8ec' }}>
                    Schedule: {chalkTalkLibraryScheduleModal.name || 'Drill'}
                  </h4>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', color: '#ccc', marginBottom: '16px' }}>
                    Practice date
                    <input
                      type="date"
                      value={practiceDateToHtmlDateValue(chalkTalkLibraryScheduleDate)}
                      onChange={(e) => setChalkTalkLibraryScheduleDate(htmlDateValueToPracticeDate(e.target.value))}
                      className="admin-input"
                      style={{ maxWidth: '200px' }}
                    />
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <button
                      type="button"
                      className="admin-button"
                      disabled={chalkTalkSaving}
                      onClick={confirmScheduleFromLibrary}
                    >
                      {chalkTalkSaving ? 'Saving…' : 'Add to schedule'}
                    </button>
                    <button
                      type="button"
                      className="admin-button"
                      onClick={() => {
                        setChalkTalkLibraryScheduleModal(null);
                        setChalkTalkLibraryScheduleDate('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </>
      </div>
      )}

      {/* SCHEDULE A GAME */}
      {activeAdminSection === 'scheduledGames' && allowedSectionSet.has('scheduledGames') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          📅 Schedule a Game Requests
          {scheduledGames.filter(r => !r.archived).length > 0 && (
            <span className="admin-section-count"> ({scheduledGames.filter(r => !r.archived).length})</span>
          )}
        </h2>
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#999' }}>
                <input type="checkbox" checked={showArchived.scheduledGames} onChange={() => toggleShowArchived('scheduledGames')} />
                Show archived
              </label>
            </div>
            {loadingScheduledGames ? (
              <p>Loading schedule requests...</p>
            ) : (showArchived.scheduledGames ? scheduledGames : scheduledGames.filter(r => !r.archived)).length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                {showArchived.scheduledGames ? 'No schedule requests (including archived).' : 'No schedule requests yet.'}
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Submitted</th>
                      <th>Team Name</th>
                      <th>Rink</th>
                      <th>Contact</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Birth Year</th>
                      <th>Travel</th>
                      <th>Game Dates</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showArchived.scheduledGames ? scheduledGames : scheduledGames.filter(r => !r.archived)).map(row => (
                      <tr key={row.id} className={row.archived ? 'admin-row-archived' : ''}>
                        <td>{row.submittedAt ? new Date(row.submittedAt).toLocaleDateString() : '—'}</td>
                        <td>{row.teamName || '—'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{row.rinkLocation || '—'}</td>
                        <td>{row.contactName || '—'}</td>
                        <td><a href={`tel:${row.phone}`} style={{ color: '#00a7a9' }}>{row.phone || '—'}</a></td>
                        <td><a href={`mailto:${row.email}`} style={{ color: '#00a7a9' }}>{row.email || '—'}</a></td>
                        <td>{row.birthYear || '—'}</td>
                        <td>{row.willingToTravel || '—'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{Array.isArray(row.gameDates) ? row.gameDates.join(', ') : row.gameDates || '—'}</td>
                        <td>
                          {row.archived ? (
                            <button type="button" className="admin-button-restore" onClick={() => handleRestore('scheduledGames', row.id)}>Restore</button>
                          ) : (
                            <button type="button" className="admin-button-archive" onClick={() => handleArchive('scheduledGames', row.id)}>Archive</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
      </div>
      )}

      {/* JOIN US SUBMISSIONS */}
      {activeAdminSection === 'submissions' && allowedSectionSet.has('submissions') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          ✋ Join Us Submissions
          {submissions.filter(r => !r.archived).length > 0 && (
            <span className="admin-section-count"> ({submissions.filter(r => !r.archived).length})</span>
          )}
        </h2>
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#999' }}>
                <input type="checkbox" checked={showArchived.submissions} onChange={() => toggleShowArchived('submissions')} />
                Show archived
              </label>
            </div>
            {loadingSubmissions ? (
              <p>Loading submissions...</p>
            ) : (showArchived.submissions ? submissions : submissions.filter(r => !r.archived)).length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                {showArchived.submissions ? 'No Join Us submissions (including archived).' : 'No Join Us submissions yet.'}
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Submitted</th>
                      <th>Parent Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Child Name</th>
                      <th>Birth Year</th>
                      <th>Position</th>
                      <th>Interest</th>
                      <th>Heard From</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showArchived.submissions ? submissions : submissions.filter(r => !r.archived)).map(row => (
                      <tr key={row.id} className={row.archived ? 'admin-row-archived' : ''}>
                        <td>{row.submittedAt ? new Date(row.submittedAt).toLocaleDateString() : '—'}</td>
                        <td>{row.parentName || '—'}</td>
                        <td><a href={`mailto:${row.parentEmail}`} style={{ color: '#00a7a9' }}>{row.parentEmail || '—'}</a></td>
                        <td><a href={`tel:${row.parentPhone}`} style={{ color: '#00a7a9' }}>{row.parentPhone || '—'}</a></td>
                        <td>{row.childName || '—'}</td>
                        <td>{row.childBirthYear || '—'}</td>
                        <td>{row.childPosition || '—'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{row.interest || '—'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{row.heardFrom || '—'}</td>
                        <td>
                          {row.archived ? (
                            <button type="button" className="admin-button-restore" onClick={() => handleRestore('submissions', row.id)}>Restore</button>
                          ) : (
                            <button type="button" className="admin-button-archive" onClick={() => handleArchive('submissions', row.id)}>Archive</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
      </div>
      )}

      {/* UPLOADED DOCUMENTS */}
      {activeAdminSection === 'uploadedDocs' && allowedSectionSet.has('uploadedDocs') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          📄 Uploaded Documents (Parents)
          {uploadedDocs.filter(r => !r.archived).length > 0 && (
            <span className="admin-section-count"> ({uploadedDocs.filter(r => !r.archived).length})</span>
          )}
        </h2>
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#999' }}>
                <input type="checkbox" checked={showArchived.uploadedDocs} onChange={() => toggleShowArchived('uploadedDocs')} />
                Show archived
              </label>
              <button
                type="button"
                className="admin-button"
                onClick={handleUploadedDocsZipAllVisible}
                disabled={uploadedDocsZipBusy || visibleUploadedDocs.length === 0}
              >
                Download all (visible) as ZIP
              </button>
              <button
                type="button"
                className="admin-button"
                onClick={handleUploadedDocsZipSelectedRows}
                disabled={uploadedDocsZipBusy || selectedVisibleUploadCount === 0}
              >
                Download selected rows ({selectedVisibleUploadCount})
              </button>
            </div>
            {uploadedDocsZipBusy && uploadedDocsZipStatus ? (
              <p style={{ color: '#aaa', marginBottom: '12px', fontSize: '0.9rem' }}>{uploadedDocsZipStatus}</p>
            ) : null}
            {!loadingUploadedDocs && visibleUploadedDocs.length > 0 && parentsWithUploadedForms.length > 0 ? (
              <div
                style={{
                  marginBottom: '16px',
                  padding: '12px 14px',
                  border: '1px solid #444',
                  borderRadius: '8px',
                  background: 'rgba(0,0,0,0.15)',
                }}
              >
                <p style={{ margin: '0 0 10px', color: '#ccc', fontSize: '0.9rem' }}>
                  Download by parent — includes every <strong>visible</strong> upload matched to that family (account UID and email on new uploads; otherwise exact name match to the parent profile).
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                  <button
                    type="button"
                    className="admin-button"
                    onClick={handleSelectAllParentsWithUploads}
                    disabled={uploadedDocsZipBusy}
                  >
                    {parentsWithUploadedForms.length > 0 &&
                    parentsWithUploadedForms.every((p) => selectedParentIdsForDocZip[p.id])
                      ? 'Clear parent selection'
                      : 'Select all parents with uploads'}
                  </button>
                  <button
                    type="button"
                    className="admin-button"
                    onClick={handleUploadedDocsZipSelectedParents}
                    disabled={
                      uploadedDocsZipBusy ||
                      rowsForSelectedParentsZip.length === 0
                    }
                  >
                    ZIP uploads from selected parents ({rowsForSelectedParentsZip.length} file
                    {rowsForSelectedParentsZip.length === 1 ? '' : 's'})
                  </button>
                </div>
                <div
                  style={{
                    maxHeight: '220px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  {parentsWithUploadedForms.map((p) => (
                    <label
                      key={p.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: uploadedDocsZipBusy ? 'default' : 'pointer',
                        color: '#ddd',
                        fontSize: '0.9rem',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedParentIdsForDocZip[p.id]}
                        onChange={() => handleToggleParentForDocZip(p.id)}
                        disabled={uploadedDocsZipBusy}
                      />
                      <span>
                        {p.name || p.displayName || '—'}
                        {p.email ? <span style={{ color: '#888' }}> ({p.email})</span> : null}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
            {loadingUploadedDocs ? (
              <p>Loading uploaded documents...</p>
            ) : visibleUploadedDocs.length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                {showArchived.uploadedDocs ? 'No uploaded documents (including archived).' : 'No documents uploaded yet.'}
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: 44, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          ref={uploadedDocSelectAllRef}
                          checked={
                            visibleUploadedDocs.length > 0 &&
                            selectedVisibleUploadCount === visibleUploadedDocs.length
                          }
                          onChange={handleToggleAllVisibleUploadSelect}
                          disabled={uploadedDocsZipBusy || visibleUploadedDocs.length === 0}
                          aria-label="Select all visible uploads"
                        />
                      </th>
                      <th>Date</th>
                      <th>Uploader</th>
                      <th>Title</th>
                      <th>Filename</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleUploadedDocs.map(row => (
                      <tr key={row.id} className={row.archived ? 'admin-row-archived' : ''}>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!!selectedUploadDocIds[row.id]}
                            onChange={() => handleToggleUploadDocSelect(row.id)}
                            disabled={uploadedDocsZipBusy}
                            aria-label={`Select upload ${row.title || row.id}`}
                          />
                        </td>
                        <td>{row.timestamp ? (typeof row.timestamp?.toDate === 'function' ? row.timestamp.toDate().toLocaleDateString() : new Date(row.timestamp).toLocaleDateString()) : '—'}</td>
                        <td>{row.uploaderName || '—'}</td>
                        <td>{row.title || '—'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{row.filename || '—'}</td>
                        <td>
                          {row.archived ? (
                            <button type="button" className="admin-button-restore" onClick={() => handleRestore('uploadedDocs', row.id)}>Restore</button>
                          ) : (
                            <button type="button" className="admin-button-archive" onClick={() => handleArchive('uploadedDocs', row.id)}>Archive</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
      </div>
      )}

      {/* GLADIATORS TV – multiple games for remote + live stream */}
      {activeAdminSection === 'gladiatorsTv' && allowedSectionSet.has('gladiatorsTv') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          📺 Gladiators TV
          {gladiatorsTvGames.length > 0 && (
            <span className="admin-section-count"> ({gladiatorsTvGames.length})</span>
          )}
        </h2>
          <>
        <p style={{ color: '#999', marginBottom: '12px', fontSize: '0.9rem' }}>
          Add archived games (YouTube replay links). Viewers pick a game from the remote; the video plays on the page with a TV turn-on animation.
        </p>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: '#ccc', fontSize: '0.85rem', marginBottom: '6px' }}>
            Live stream URL (when we have a live game)
          </label>
          <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '8px' }}>
            Paste a YouTube live URL (or other embeddable link). When set, the LIVE channel will show the stream on your site instead of sending viewers to Sideline HD.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <input
              type="url"
              className="admin-input"
              placeholder="https://www.youtube.com/watch?v=... or YouTube live URL"
              value={gladiatorsTvLiveStreamUrl}
              onChange={(e) => setGladiatorsTvLiveStreamUrl(e.target.value)}
              style={{ flex: '1', minWidth: '200px', maxWidth: '480px' }}
            />
            <button
              type="button"
              className="admin-button"
              disabled={gladiatorsTvSaving}
              onClick={async () => {
                setGladiatorsTvSaving(true);
                try {
                  await setDoc(
                    doc(db, 'settings', 'gladiatorsTv'),
                    { liveStreamUrl: gladiatorsTvLiveStreamUrl.trim() },
                    { merge: true }
                  );
                } catch (err) {
                  console.error(err);
                  alert('Failed to save live stream URL.');
                } finally {
                  setGladiatorsTvSaving(false);
                }
              }}
            >
              Save live URL
            </button>
          </div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: '#ccc', fontSize: '0.85rem', marginBottom: '6px' }}>
            Live scoreboard team (Gladiators TV page)
          </label>
          <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '8px' }}>
            Pick which age group&apos;s in-progress game appears in the Live scoreboard block on the public Gladiators
            TV page (12U vs 14U Firestore paths).
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <select
              className="admin-input"
              style={{ minWidth: '160px', maxWidth: '240px' }}
              value={gladiatorsTvLiveScoreboardTeamId}
              onChange={(e) => setGladiatorsTvLiveScoreboardTeamId(e.target.value)}
            >
              {TEAM_STATS_TEAMS.map(({ teamId, label }) => (
                <option key={teamId} value={teamId}>
                  {label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="admin-button"
              disabled={gladiatorsTvSaving}
              onClick={async () => {
                setGladiatorsTvSaving(true);
                try {
                  await setDoc(
                    doc(db, 'settings', 'gladiatorsTv'),
                    { liveScoreboardTeamId: gladiatorsTvLiveScoreboardTeamId },
                    { merge: true }
                  );
                } catch (err) {
                  console.error(err);
                  alert('Failed to save live scoreboard team.');
                } finally {
                  setGladiatorsTvSaving(false);
                }
              }}
            >
              Save scoreboard team
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
          <input
            type="text"
            className="admin-input"
            placeholder="Game label (e.g. vs Eagles – Jan 15)"
            value={gladiatorsTvNewLabel}
            onChange={(e) => setGladiatorsTvNewLabel(e.target.value)}
            style={{ width: '200px', minWidth: '140px' }}
          />
          <input
            type="text"
            className="admin-input"
            placeholder="YouTube URL"
            value={gladiatorsTvNewUrl}
            onChange={(e) => setGladiatorsTvNewUrl(e.target.value)}
            style={{ flex: '1', minWidth: '200px', maxWidth: '400px' }}
          />
          <button
            type="button"
            className="admin-button"
            disabled={gladiatorsTvSaving || !gladiatorsTvNewLabel.trim() || !gladiatorsTvNewUrl.trim()}
            onClick={async () => {
              const label = gladiatorsTvNewLabel.trim();
              const youtubeUrl = gladiatorsTvNewUrl.trim();
              if (!label || !youtubeUrl) return;
              const newGame = { id: `g${Date.now()}`, label, youtubeUrl };
              const next = [...gladiatorsTvGames, newGame];
              setGladiatorsTvGames(next);
              setGladiatorsTvNewLabel('');
              setGladiatorsTvNewUrl('');
              setGladiatorsTvSaving(true);
              try {
                await setDoc(doc(db, 'settings', 'gladiatorsTv'), { games: next }, { merge: true });
              } catch (err) {
                console.error(err);
                setGladiatorsTvGames(gladiatorsTvGames);
                alert('Failed to add game.');
              } finally {
                setGladiatorsTvSaving(false);
              }
            }}
          >
            Add game
          </button>
        </div>
        {gladiatorsTvGames.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {gladiatorsTvGames.map((g) => (
              <li key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', padding: '8px', background: '#1a1a1a', borderRadius: '6px' }}>
                <span style={{ flex: 1, color: '#fff', fontSize: '0.9rem' }}>{g.label}</span>
                <span style={{ color: '#999', fontSize: '0.8rem', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.youtubeUrl}</span>
                <button
                  type="button"
                  className="admin-button"
                  style={{ padding: '4px 10px', fontSize: '0.85rem' }}
                  disabled={gladiatorsTvSaving}
                  onClick={async () => {
                    const next = gladiatorsTvGames.filter((x) => x.id !== g.id);
                    setGladiatorsTvGames(next);
                    setGladiatorsTvSaving(true);
                    try {
                      await setDoc(doc(db, 'settings', 'gladiatorsTv'), { games: next }, { merge: true });
                    } catch (err) {
                      setGladiatorsTvGames(gladiatorsTvGames);
                      alert('Failed to remove.');
                    } finally {
                      setGladiatorsTvSaving(false);
                    }
                  }}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
          </>
      </div>
      )}

      {/* LIVE SCOREBOARD RESET */}
      {activeAdminSection === 'practiceLines' && allowedSectionSet.has('practiceLines') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">Practice line builder</h2>
        <p style={{ color: '#999', marginBottom: '16px', fontSize: '0.9rem', maxWidth: '720px' }}>
          Arrange forward lines and D-pairs from your Stat Tracker roster. Assign colors for practice pinnies. Layout syncs to Firestore per team (12U / 14U).
        </p>
        <PracticeLineBoard />
      </div>
      )}

      {activeAdminSection === 'liveScoreboard' && allowedSectionSet.has('liveScoreboard') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">📺 Live Scoreboard</h2>
        <p style={{ color: '#999', marginBottom: '12px', fontSize: '0.9rem' }}>
          If the Live Scoreboard is stuck on an old or ended game (e.g. after testing), reset it so all Live tabs show &quot;No game right now.&quot;
        </p>
        <button
          type="button"
          className="admin-button"
          onClick={() => {
            const teamId = 'glass-city-gladiators-12u';
            const key = `gcg-live-game:${teamId}`;
            if (typeof localStorage !== 'undefined') {
              localStorage.setItem(key, '');
              alert('Live Scoreboard reset. Any open Live tabs will now show "No game right now."');
            }
          }}
        >
          Reset Live Scoreboard to no game
        </button>
      </div>
      )}

      {/* MASTER RECORD (GAME HISTORY) */}
      {activeAdminSection === 'gameHistory' && allowedSectionSet.has('gameHistory') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          📊 Master Record (Game History)
          {gameHistory.length > 0 && (
            <span className="admin-section-count"> ({gameHistory.length})</span>
          )}
        </h2>
          <>
            <p style={{ color: '#999', marginBottom: '16px', fontSize: '0.9rem' }}>
              Ended games from the Stat Tracker are saved here. Use this data for analytics and future roster stats. Not public yet.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              <button
                type="button"
                className="admin-button"
                disabled={!gameHistory.length}
                onClick={handleExportGameHistoryGamesCSV}
                title="One row per game (score, shots, goalie, Gazette)"
              >
                Export games CSV
              </button>
              <button
                type="button"
                className="admin-button"
                disabled={!gameHistory.length}
                onClick={handleExportGameHistoryPlayersCSV}
                title="One row per player per game"
              >
                Export player stats CSV
              </button>
              <button
                type="button"
                className="admin-button"
                disabled={!aggregatedRoster.length}
                onClick={handleExportGameHistorySeasonTotalsCSV}
                title="Season totals from the roster table below"
              >
                Export season totals CSV
              </button>
            </div>
            {canAdminChooseStatsRoot() && (
              <div
                style={{
                  marginBottom: 16,
                  padding: '12px 14px',
                  borderRadius: 8,
                  background: adminStatsRoot === 'stats' ? 'rgba(180,60,60,0.2)' : 'rgba(0,167,169,0.12)',
                  border: `1px solid ${adminStatsRoot === 'stats' ? '#c44' : '#00a7a9'}`,
                }}
              >
                <div style={{ fontSize: '0.9rem', marginBottom: 8, color: '#ddd' }}>
                  Stats database for Admin tools:{' '}
                  <strong style={{ color: adminStatsRoot === 'stats' ? '#f88' : '#00a7a9' }}>
                    {adminStatsRoot}/
                  </strong>
                  {adminStatsRoot === 'stats'
                    ? ' — live website data. Delete test games here, then switch back to statsDev.'
                    : ' — local sandbox only. Switch to production to remove games that leaked onto the live site.'}
                </div>
                <div className="btn-group" role="group" aria-label="Admin stats root">
                  <button
                    type="button"
                    className={`btn btn-sm ${adminStatsRoot === 'statsDev' ? 'btn-teal' : 'btn-outline-light'}`}
                    onClick={() => handleAdminStatsRootChange('statsDev')}
                  >
                    Sandbox (statsDev)
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${adminStatsRoot === 'stats' ? 'btn-danger' : 'btn-outline-light'}`}
                    onClick={() => handleAdminStatsRootChange('stats')}
                  >
                    Production (stats)
                  </button>
                </div>
              </div>
            )}
            {loadingGameHistory ? (
              <p>Loading game history...</p>
            ) : gameHistory.length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                No games in the master record yet. End games in the Stat Tracker to add them, or use &quot;Sync to master record&quot; for already-archived games.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: '32px' }}></th>
                      <th>Team</th>
                      <th>Date</th>
                      <th>Opponent</th>
                      <th>Location</th>
                      <th>Score</th>
                      <th>Shots</th>
                      <th>Ended at</th>
                      <th>Players</th>
                      <th>Show on home</th>
                      <th style={{ width: '110px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gameHistory.map(row => {
                      const details = row.details || {};
                      const gd = row.gameData || {};
                      const score = gd.score || {};
                      const shots = gd.shots || {};
                      const lineupList = Array.isArray(row.lineup) ? row.lineup : [];
                      const skaters = lineupList.filter(p => !p.isGoalie);
                      const goalie = lineupList.find(p => p.isGoalie);
                      const rowKey = `${row.teamId || ''}:${row.id}`;
                      const isExpanded = expandedGameId === rowKey;
                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            style={{ cursor: 'pointer', backgroundColor: isExpanded ? 'rgba(0,167,169,0.1)' : undefined }}
                            onClick={() => setExpandedGameId(isExpanded ? null : rowKey)}
                          >
                            <td style={{ verticalAlign: 'middle', color: '#00a7a9' }}>
                              {isExpanded ? '▼' : '▶'}
                            </td>
                            <td>{row.teamLabel || getTeamStatsLabel(row.teamId) || '—'}</td>
                            <td>{details.date ? new Date(details.date).toLocaleDateString() : '—'}</td>
                            <td style={{ fontWeight: 'bold' }}>{details.opponent || '—'}</td>
                            <td style={{ fontSize: '0.85rem' }}>{details.location || '—'}</td>
                            <td>
                              <strong style={{ color: '#00a7a9' }}>{score.us ?? 0}</strong>
                              <span style={{ color: '#666', margin: '0 4px' }}>–</span>
                              <strong>{score.them ?? 0}</strong>
                            </td>
                            <td style={{ fontSize: '0.9rem' }}>
                              {shots.us ?? 0} – {shots.them ?? 0}
                            </td>
                            <td style={{ fontSize: '0.85rem', color: '#999' }}>
                              {row.endedAt ? new Date(row.endedAt).toLocaleString() : '—'}
                            </td>
                            <td>{skaters.length}</td>
                            <td onClick={(e) => e.stopPropagation()} style={{ verticalAlign: 'middle' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={row.showOnHome !== false}
                                  onChange={(e) => handleToggleShowOnHome(e, row)}
                                />
                                <span style={{ fontSize: '0.85rem', color: '#999' }}>Home</span>
                              </label>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                disabled={deletingGameKey === rowKey}
                                onClick={() => handleDeleteGamePermanently(row)}
                              >
                                {deletingGameKey === rowKey ? 'Deleting…' : 'Delete'}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={11} style={{ padding: 0, borderTop: 'none', verticalAlign: 'top' }}>
                                <div style={{ padding: '12px 16px', background: '#1a1a1a', marginLeft: '8px', marginRight: '8px', marginBottom: '8px', borderRadius: '6px' }}>
                                  <div style={{ fontSize: '0.85rem', color: '#999', marginBottom: '8px' }}>Player stats (this game)</div>
                                  <table className="admin-table" style={{ margin: 0, fontSize: '0.9rem' }}>
                                    <thead>
                                      <tr>
                                        <th>Name</th>
                                        <th>#</th>
                                        <th>Pos</th>
                                        <th>G</th>
                                        <th>A</th>
                                        <th>P</th>
                                        <th>+/-</th>
                                        <th>Pen</th>
                                        <th>PIM</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {skaters.map((p, idx) => (
                                        <tr key={`${p.name}-${idx}`}>
                                          <td style={{ fontWeight: '600' }}>{p.name || '—'}</td>
                                          <td>{p.number ?? '—'}</td>
                                          <td>{p.position || '—'}</td>
                                          <td>{p.goals ?? 0}</td>
                                          <td>{p.assists ?? 0}</td>
                                          <td style={{ color: '#00a7a9', fontWeight: '600' }}>{p.points ?? 0}</td>
                                          <td>{p.plusMinus != null ? (p.plusMinus > 0 ? '+' : '') + p.plusMinus : '—'}</td>
                                          <td>{p.penalties ?? 0}</td>
                                          <td>{p.pim != null ? Number(p.pim).toFixed(2) : '—'}</td>
                                        </tr>
                                      ))}
                                      {goalie && (() => {
                                        const saves = gd.goalieSaves ?? 0;
                                        const ga = score.them ?? 0;
                                        const shotsAgainst = saves + ga;
                                        const savePct = shotsAgainst > 0 ? ((saves / shotsAgainst) * 100).toFixed(2) : '—';
                                        return (
                                          <tr style={{ borderTop: '1px solid #333' }}>
                                            <td style={{ fontWeight: '600' }}>{goalie.name || '—'} <span style={{ color: '#888', fontSize: '0.8rem' }}>(G)</span></td>
                                            <td>{goalie.number ?? '—'}</td>
                                            <td>G</td>
                                            <td colSpan={6} style={{ color: '#888' }}>
                                              Saves: {saves} · GA: {ga} · SV%: {savePct}{typeof savePct === 'string' && savePct !== '—' ? '%' : ''}
                                            </td>
                                          </tr>
                                        );
                                      })()}
                                      {lineupList.length === 0 && (
                                        <tr>
                                          <td colSpan={9} style={{ color: '#999' }}>No lineup data</td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {gameHistory.length > 0 && aggregatedRoster.length > 0 && (
              <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #333' }}>
                <h3 style={{ color: '#00a7a9', marginBottom: '12px', fontSize: '1.1rem' }}>Player Roster Master Stats</h3>
                <p style={{ color: '#999', marginBottom: '12px', fontSize: '0.85rem' }}>
                  Totals aggregated from all games in the master record. Skaters sorted by points; goalies at bottom. Goalie saves and SV% are for games they were in net (one goalie per game).
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>#</th>
                        <th>Pos</th>
                        <th>GP</th>
                        <th>G</th>
                        <th>A</th>
                        <th>P</th>
                        <th>+/-</th>
                        <th>Pen</th>
                        <th>PIM</th>
                        <th>Saves</th>
                        <th>SV%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aggregatedRoster.map((p, idx) => {
                        const shotsAgainst = (p.saves || 0) + (p.goalsAgainst || 0);
                        const savePct = p.isGoalie && shotsAgainst > 0 ? (((p.saves || 0) / shotsAgainst) * 100).toFixed(2) + '%' : '—';
                        return (
                          <tr key={`${p.name}-${p.number}-${idx}`} style={p.isGoalie ? { borderTop: '1px solid #333' } : undefined}>
                            <td style={{ fontWeight: '600' }}>{p.name}{p.isGoalie && <span style={{ color: '#888', fontSize: '0.8rem', marginLeft: '4px' }}>(G)</span>}</td>
                            <td>{p.number}</td>
                            <td>{p.position}</td>
                            <td>{p.gamesPlayed}</td>
                            <td>{p.isGoalie ? '—' : p.goals}</td>
                            <td>{p.isGoalie ? '—' : p.assists}</td>
                            <td style={p.isGoalie ? undefined : { color: '#00a7a9', fontWeight: '600' }}>{p.isGoalie ? '—' : p.points}</td>
                            <td>{p.isGoalie ? '—' : (p.plusMinus > 0 ? '+' : '') + p.plusMinus}</td>
                            <td>{p.isGoalie ? '—' : p.penalties}</td>
                            <td>{p.isGoalie ? '—' : Number(p.pim || 0).toFixed(2)}</td>
                            <td style={{ color: '#888' }}>{p.isGoalie ? p.saves : '—'}</td>
                            <td style={{ color: '#888' }}>{p.isGoalie ? savePct : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
      </div>
      )}

      {/* GLADIATOR GAZETTE — pull published stories */}
      {activeAdminSection === 'gladiatorGazette' && allowedSectionSet.has('gladiatorGazette') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          Gladiator Gazette
          {gazetteEntries.length > 0 && (
            <span className="admin-section-count"> ({gazetteEntries.length})</span>
          )}
        </h2>
        <p style={{ color: '#999', marginBottom: '16px', fontSize: '0.9rem' }}>
          Published game stories shown on Live Scoreboard, Gladiators TV, and the public Gazette page.
          Use <strong>Pull Gazette</strong> to remove a story if it needs to come down. Game stats stay in the master record.
          {adminStatsRoot === 'statsDev' && (
            <span style={{ display: 'block', marginTop: 8, color: '#b4975a' }}>
              Currently viewing <code>statsDev/</code>. Switch to Production under Game history if you need to pull a live-site Gazette.
            </span>
          )}
          {adminStatsRoot === 'stats' && (
            <span style={{ display: 'block', marginTop: 8, color: '#f88' }}>
              Currently viewing <strong>production</strong> <code>stats/</code> Gazettes.
            </span>
          )}
        </p>
        {loadingGazettes ? (
          <p>Loading Gazettes…</p>
        ) : gazetteEntries.length === 0 ? (
          <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
            No published Gazettes in this environment yet.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Date</th>
                  <th>Opponent</th>
                  <th>Headline</th>
                  <th style={{ width: '140px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {gazetteEntries.map((entry) => {
                  const details = entry.details || {};
                  const removeKey = `${entry.teamId}:${entry.id}`;
                  return (
                    <tr key={removeKey}>
                      <td>{entry.teamLabel || getTeamStatsLabel(entry.teamId)}</td>
                      <td>{details.date ? new Date(details.date).toLocaleDateString() : '—'}</td>
                      <td style={{ fontWeight: 'bold' }}>{details.opponent || '—'}</td>
                      <td style={{ maxWidth: 320 }}>
                        <div style={{ fontWeight: 600 }}>{entry.recap?.headline || '—'}</div>
                        {entry.recap?.fanQuote?.displayName && (
                          <div style={{ fontSize: '0.8rem', color: '#999', marginTop: 4 }}>
                            Fan quote: {entry.recap.fanQuote.displayName}
                          </div>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="admin-button-archive"
                          disabled={removingGazetteId === removeKey}
                          onClick={() => handleRemoveGazette(entry)}
                        >
                          {removingGazetteId === removeKey ? 'Pulling…' : 'Pull Gazette'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* MEMORY GAME LEADERBOARD */}
      {activeAdminSection === 'memoryLeaderboard' && allowedSectionSet.has('memoryLeaderboard') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          🃏 Memory Game Leaderboard (Live Scoreboard)
          {memoryLeaderboard.length > 0 && (
            <span className="admin-section-count"> ({memoryLeaderboard.length})</span>
          )}
        </h2>
          <>
            <p style={{ color: '#999', marginBottom: '16px', fontSize: '0.9rem' }}>
              Entries from the Gladiator Memory Game on the Live Scoreboard. Remove entries (e.g. foul language) with the Remove button.
            </p>
            {loadingMemoryLeaderboard ? (
              <p>Loading leaderboard...</p>
            ) : memoryLeaderboard.length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                No leaderboard entries yet.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Moves</th>
                      <th>Time</th>
                      <th>Submitted</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memoryLeaderboard.map((row, i) => (
                      <tr key={row.id}>
                        <td>{i + 1}</td>
                        <td style={{ fontWeight: '600' }}>{row.name || '—'}</td>
                        <td>{row.moves ?? '—'}</td>
                        <td>{row.timeSeconds != null ? `${row.timeSeconds}s` : '—'}</td>
                        <td style={{ fontSize: '0.85rem', color: '#999' }}>
                          {row.createdAt ? (typeof row.createdAt?.toDate === 'function' ? row.createdAt.toDate() : new Date(row.createdAt)).toLocaleString() : '—'}
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => handleRemoveMemoryLeaderboardEntry(row.id)}
                            disabled={removingEntryId === row.id}
                            style={{
                              background: '#dc2626',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 10px',
                              fontSize: '0.85rem',
                              cursor: removingEntryId === row.id ? 'not-allowed' : 'pointer',
                              opacity: removingEntryId === row.id ? 0.7 : 1
                            }}
                          >
                            {removingEntryId === row.id ? 'Removing…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
      </div>
      )}

      {activeAdminSection === 'parentPin' && allowedSectionSet.has('parentPin') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">🔐 Parent PIN Settings</h2>
        <p><strong>Current PIN:</strong> {currentPin || 'Not set'}</p>
        <div className="pin-update">
          <input
            type="text"
            placeholder="New PIN code"
            value={newPin}
            onChange={e => setNewPin(e.target.value)}
          />
          <button onClick={handlePinUpdate}>Create/Update PIN</button>
        </div>
      </div>
      )}

      {activeAdminSection === 'parentSurveys' && allowedSectionSet.has('parentSurveys') && (
        <ParentSurveysAdmin parents={parents} />
      )}

      {activeAdminSection === 'parentAccounts' && allowedSectionSet.has('parentAccounts') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          📋 Manage Parent Accounts &amp; Team Dues
          {parents.length > 0 && (
            <span className="admin-section-count">
              {' '}
              (
              {parents.filter((p) => parentHasProfileFields(p) && parentPortalEnabled(p)).length} active portal
              {parents.some((p) => !parentHasProfileFields(p)) &&
                ` · ${parents.filter((p) => !parentHasProfileFields(p)).length} no profile`}
              {!showDisabledPortalParents &&
                parents.some((p) => parentHasProfileFields(p) && !parentPortalEnabled(p)) &&
                ` · ${parents.filter((p) => parentHasProfileFields(p) && !parentPortalEnabled(p)).length} disabled (hidden)`}
              )
            </span>
          )}
        </h2>
          <>
        <p style={{ color: '#999', marginBottom: '12px', fontSize: '0.9rem', maxWidth: '720px' }}>
          Parent profiles stay in Firestore (name, email, Stripe customer id if present). Use <strong style={{ color: '#ccc' }}>Edit</strong> under Team dues to add line items (warm-ups, gloves, dues), amounts, due dates, and mark each line paid when money is received. Remove a mistaken charge with <strong style={{ color: '#ccc' }}>Delete</strong> on that row in Edit, or from <strong style={{ color: '#ccc' }}>All line items</strong> (Actions column). Families can upload payment proof photos per line. Families see this under <strong style={{ color: '#ccc' }}>Team Dues</strong> in their portal. Use <strong style={{ color: '#ccc' }}>Disable access</strong> for families who have left the team — those rows are hidden here unless you turn on <strong style={{ color: '#ccc' }}>Show portal-disabled families</strong> (so you can turn access back on). Rows with no name or email are usually not Gladiator families: they are Firebase users created for one-off Stripe checkout (tryouts, Gladiator Games, etc.). Those are hidden from this table by default; turn on <strong style={{ color: '#ccc' }}>Show no-profile records</strong> if you need to inspect them.
        </p>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
            marginBottom: '14px',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#999', fontSize: '0.8rem', flex: '1', minWidth: '200px' }}>
            Search (parent name or email; also matches charge text, UID, or Stripe id)
            <input
              type="search"
              className="admin-input"
              placeholder="Type to filter…"
              value={parentAccountsFilter}
              onChange={(e) => setParentAccountsFilter(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              aria-label="Search parents and line items"
            />
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#aaa',
              fontSize: '0.85rem',
              cursor: 'pointer',
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={showIncompleteParentProfiles}
              onChange={(e) => setShowIncompleteParentProfiles(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
            />
            Show no-profile records
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#aaa',
              fontSize: '0.85rem',
              cursor: 'pointer',
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={showDisabledPortalParents}
              onChange={(e) => setShowDisabledPortalParents(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
            />
            Show portal-disabled families
          </label>
          <span style={{ color: '#888', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
            {parentAccountsDuesView === 'families'
              ? !parentAccountsFilter.trim() && filteredSortedParents.length === parentAccountsPoolSize
                ? `${parentAccountsPoolSize} famil${parentAccountsPoolSize === 1 ? 'y' : 'ies'}`
                : `${filteredSortedParents.length} of ${parentAccountsPoolSize} shown`
              : `${filteredSortedFlatLineItems.length} line item${filteredSortedFlatLineItems.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className="admin-parent-dues-toolbar">
          <div className="admin-parent-dues-view-toggle" role="group" aria-label="Dues table view">
            <button
              type="button"
              className={`admin-segment-button${parentAccountsDuesView === 'families' ? ' is-active' : ''}`}
              onClick={() => setParentAccountsDuesView('families')}
            >
              Families
            </button>
            <button
              type="button"
              className={`admin-segment-button${parentAccountsDuesView === 'lineItems' ? ' is-active' : ''}`}
              onClick={() => setParentAccountsDuesView('lineItems')}
            >
              All line items
            </button>
          </div>
          <div className="admin-parent-dues-status">
            {duesOverviewLoading ? (
              <span style={{ color: '#888', fontSize: '0.85rem' }}>Loading dues…</span>
            ) : duesOverviewError ? (
              <span style={{ color: '#e57373', fontSize: '0.85rem' }}>{duesOverviewError}</span>
            ) : (
              <span style={{ color: '#888', fontSize: '0.85rem' }}>Dues data loaded</span>
            )}
            <button
              type="button"
              className="admin-button"
              style={{ padding: '4px 10px', fontSize: '0.8rem', marginLeft: '8px' }}
              onClick={() => void reloadDuesFeeOverview()}
              disabled={duesOverviewLoading}
            >
              Refresh balances
            </button>
          </div>
        </div>
        <p style={{ color: '#777', fontSize: '0.8rem', marginBottom: '12px' }}>
          Click a column header to sort. <strong style={{ color: '#aaa' }}>Balance</strong> / <strong style={{ color: '#aaa' }}>Late</strong> use unpaid totals;{' '}
          <strong style={{ color: '#aaa' }}>Late</strong> counts unpaid lines whose due date is before today. Portal: ascending shows{' '}
          <strong style={{ color: '#aaa' }}>active</strong> accounts first.
        </p>
        <div className="admin-parent-dues-export-row">
          <button onClick={handleExportOrders} className="export-button" type="button">
            📤 Export Orders to CSV
          </button>
          <button onClick={handleExportDuesLineItemsCSV} className="export-button" type="button" title="One row per fee line for every family in scope (no-profile toggle applies)">
            📤 Export dues (all line items) CSV
          </button>
          <button onClick={handleExportDuesFamilySummaryCSV} className="export-button" type="button" title="One row per family with balance and late counts">
            📤 Export family balances CSV
          </button>
        </div>

        <div className="admin-bulk-fee-panel">
          <h3 className="admin-bulk-fee-title">Bulk apply fee line item</h3>
          <p className="admin-bulk-fee-intro">
            Adds the same unpaid line to many families at once. Use{' '}
            <strong style={{ color: '#aaa' }}>All with name/email</strong> for a full roster, or narrow the table with the filter and choose{' '}
            <strong style={{ color: '#aaa' }}>Current table</strong> for a subset. Per-family edits and deletes still work in each row’s{' '}
            <strong style={{ color: '#aaa' }}>Edit</strong> panel.
          </p>
          <div className="admin-bulk-fee-fields">
            <div className="admin-bulk-fee-field">
              <label htmlFor="bulk-fee-description">Description</label>
              <input
                id="bulk-fee-description"
                type="text"
                className="admin-input"
                placeholder="e.g. Team warm-ups"
                value={bulkFeeDescription}
                onChange={(e) => setBulkFeeDescription(e.target.value)}
                disabled={bulkFeeApplying}
              />
            </div>
            <div className="admin-bulk-fee-field">
              <label htmlFor="bulk-fee-amount">Amount ($)</label>
              <input
                id="bulk-fee-amount"
                type="number"
                step="0.01"
                min="0"
                className="admin-input"
                placeholder="0.00"
                value={bulkFeeAmount}
                onChange={(e) => setBulkFeeAmount(e.target.value)}
                disabled={bulkFeeApplying}
              />
            </div>
            <div className="admin-bulk-fee-field">
              <label htmlFor="bulk-fee-season">Season (optional)</label>
              <input
                id="bulk-fee-season"
                type="text"
                className="admin-input"
                placeholder="2025–26"
                value={bulkFeeSeason}
                onChange={(e) => setBulkFeeSeason(e.target.value)}
                disabled={bulkFeeApplying}
              />
            </div>
            <div className="admin-bulk-fee-field">
              <label htmlFor="bulk-fee-due">Due date (optional)</label>
              <input
                id="bulk-fee-due"
                type="date"
                className="admin-input"
                value={bulkFeeDueDate}
                onChange={(e) => setBulkFeeDueDate(e.target.value)}
                disabled={bulkFeeApplying}
              />
            </div>
          </div>

          <div className="admin-bulk-fee-divider" aria-hidden="true" />

          <p className="admin-bulk-fee-options-title">Apply to</p>
          <div className={`admin-bulk-fee-options${bulkFeeApplying ? ' is-applying' : ''}`}>
            <label className="admin-bulk-fee-option">
              <input
                type="radio"
                name="bulkFeeScope"
                checked={bulkFeeScope === 'profile'}
                onChange={() => setBulkFeeScope('profile')}
                disabled={bulkFeeApplying}
              />
              <span>
                <strong>All families with name/email</strong>
                <span style={{ color: '#888' }}> ({parentsWithProfileCount})</span>
              </span>
            </label>
            <label className="admin-bulk-fee-option">
              <input
                type="radio"
                name="bulkFeeScope"
                checked={bulkFeeScope === 'table'}
                onChange={() => setBulkFeeScope('table')}
                disabled={bulkFeeApplying}
              />
              <span>
                <strong>Current table only</strong>
                <span style={{ display: 'block', color: '#999', fontSize: '0.82rem', marginTop: '2px' }}>
                  Uses search filter, “no-profile” toggle, and sort ({filteredSortedParents.length} shown)
                </span>
              </span>
            </label>
            <label className="admin-bulk-fee-option admin-bulk-fee-option--muted">
              <input
                type="checkbox"
                checked={bulkFeeSkipDuplicates}
                onChange={(e) => setBulkFeeSkipDuplicates(e.target.checked)}
                disabled={bulkFeeApplying}
              />
              <span>Skip families that already have this description and season label</span>
            </label>
          </div>

          <div className="admin-bulk-fee-actions">
            <button
              type="button"
              className="admin-button"
              onClick={handleBulkApplyFeeItems}
              disabled={bulkFeeApplying || loading || bulkFeeTargets.length === 0}
            >
              {bulkFeeApplying ? 'Applying…' : 'Add line to families'}
            </button>
            <span className="admin-bulk-fee-status">
              {bulkFeeStatus || `Ready: ${bulkFeeTargets.length} famil${bulkFeeTargets.length === 1 ? 'y' : 'ies'}`}
            </span>
          </div>
        </div>

        {loading ? (
          <p>Loading parent records...</p>
        ) : parentAccountsDuesView === 'lineItems' ? (
          <div className="admin-table-wrap">
            <table className="admin-table admin-table-dues-line-items">
              <thead>
                <tr>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleLineItemsSort('parentName')}
                    title="Sort by parent name"
                  >
                    Parent{lineItemsSortIndicator('parentName')}
                  </th>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleLineItemsSort('description')}
                    title="Sort by description"
                  >
                    Description{lineItemsSortIndicator('description')}
                  </th>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleLineItemsSort('amount')}
                    title="Sort by amount"
                  >
                    Amount ($){lineItemsSortIndicator('amount')}
                  </th>
                  <th>Season / note</th>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleLineItemsSort('dueDate')}
                    title="Sort by due date"
                  >
                    Due date{lineItemsSortIndicator('dueDate')}
                  </th>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleLineItemsSort('paid')}
                    title="Sort by paid"
                  >
                    Paid{lineItemsSortIndicator('paid')}
                  </th>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleLineItemsSort('daysLate')}
                    title="Sort by days late"
                  >
                    Days late{lineItemsSortIndicator('daysLate')}
                  </th>
                  <th>Parent email</th>
                  <th>Portal</th>
                  <th>Proof</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSortedFlatLineItems.length === 0 ? (
                  <tr>
                    <td colSpan={11} style={{ color: '#777' }}>
                      No line items match this search, or no charges have been added yet.
                    </td>
                  </tr>
                ) : (
                  filteredSortedFlatLineItems.map((row) => {
                    const portalActive = row.parent.accessEnabled !== false;
                    const item = row.item;
                    const familyLabel =
                      row.parent.name || row.parent.displayName || row.parent.email || 'this family';
                    const desc = (item.description || '').trim() || '(no description)';
                    const amtStr = Number.isFinite(Number(item.amount))
                      ? Number(item.amount).toFixed(2)
                      : '?';
                    const deleteConfirm =
                      `Delete this charge for ${familyLabel}?\n\n` +
                      `"${desc}" — $${amtStr}\n\n` +
                      'Parents will no longer see it. This cannot be undone.';
                    return (
                      <tr
                        key={row.key}
                        className={!parentHasProfileFields(row.parent) ? 'admin-row-incomplete-profile' : ''}
                      >
                        <td>{row.parent.name || row.parent.displayName || '—'}</td>
                        <td>{item.description || '—'}</td>
                        <td>{Number.isFinite(Number(item.amount)) ? Number(item.amount).toFixed(2) : '—'}</td>
                        <td>{item.seasonLabel || '—'}</td>
                        <td>{item.dueDate || '—'}</td>
                        <td>{item.paid ? 'Yes' : 'No'}</td>
                        <td>{row.daysLate > 0 ? row.daysLate : '—'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{row.parent.email || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                          {portalActive ? 'Active' : 'Disabled'}
                        </td>
                        <td style={{ maxWidth: '120px', fontSize: '0.8rem' }}>
                          {item.paymentProofUrl ? (
                            <a
                              href={item.paymentProofUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#7fd0d2', wordBreak: 'break-all' }}
                            >
                              View
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            type="button"
                            className="admin-button-archive"
                            style={{ padding: '4px 10px', fontSize: '0.85rem' }}
                            onClick={() => handleDeleteFeeItem(item.id, row.parent.id, deleteConfirm)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table admin-table-parent-accounts">
              <thead>
                <tr>
                  <th
                    role="columnheader"
                    className="admin-table-sortable admin-th-portal"
                    onClick={() => toggleParentSort('portal')}
                    title="Sort by portal access"
                  >
                    Portal{parentSortIndicator('portal')}
                  </th>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleParentSort('balance')}
                    title="Sort by unpaid balance"
                  >
                    Balance{parentSortIndicator('balance')}
                  </th>
                  <th title="Unpaid line item count">Unpaid #</th>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleParentSort('late')}
                    title="Sort by late unpaid lines"
                  >
                    Late{parentSortIndicator('late')}
                  </th>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleParentSort('nextDue')}
                    title="Sort by earliest unpaid due date"
                  >
                    Next due{parentSortIndicator('nextDue')}
                  </th>
                  <th>Team dues</th>
                  <th>Roster kids</th>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleParentSort('name')}
                    title="Sort by name"
                  >
                    Name{parentSortIndicator('name')}
                  </th>
                  <th
                    role="columnheader"
                    className="admin-table-sortable"
                    onClick={() => toggleParentSort('email')}
                    title="Sort by email"
                  >
                    Email{parentSortIndicator('email')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredSortedParents.map((p) => {
                  const portalActive = p.accessEnabled !== false;
                  const feesOpen = feeEditorParentId === p.id;
                  const linkedOpen = linkedPlayersEditorParentId === p.id;
                  const hasProfile = parentHasProfileFields(p);
                  const feeLinesForBalance =
                    feeEditorParentId === p.id && !feeEditorLoading ? feeEditorRows : feeItemsByParentId[p.id];
                  const feeSum = computeParentFeeSummary(feeLinesForBalance);
                  const linkedSummary = Array.isArray(p.linkedPlayers) && p.linkedPlayers.length
                    ? p.linkedPlayers
                        .map((lp) => {
                          const entry = rosterLinkOptions.find(
                            (r) => r.teamId === lp.teamId && r.playerName === lp.playerName
                          );
                          return entry ? entry.label : `${lp.teamId} ${lp.playerName}`;
                        })
                        .join('; ')
                    : '';
                  return (
                    <React.Fragment key={p.id}>
                      <tr
                        className={[
                          portalActive ? '' : 'admin-row-archived',
                          hasProfile ? '' : 'admin-row-incomplete-profile',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <td className="admin-td-portal">
                          <div className="admin-td-portal-inner">
                            <span
                              className="admin-portal-status-badge"
                              style={{
                                background: portalActive ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                                color: portalActive ? '#81c784' : '#e57373',
                              }}
                            >
                              {portalActive ? 'Active' : 'Disabled'}
                            </span>
                            <button
                              type="button"
                              className={portalActive ? 'admin-button-archive' : 'admin-button-restore'}
                              onClick={() => handleSetParentPortalAccess(p.id, p.name || p.displayName, !portalActive)}
                            >
                              {portalActive ? 'Disable access' : 'Enable access'}
                            </button>
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>${feeSum.balance.toFixed(2)}</td>
                        <td style={{ textAlign: 'center' }}>{feeSum.unpaidCount}</td>
                        <td style={{ textAlign: 'center' }}>
                          {feeSum.lateCount > 0 ? (
                            <span style={{ color: '#e57373', fontWeight: 700 }}>{feeSum.lateCount}</span>
                          ) : (
                            <span style={{ color: '#888' }}>0</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem', color: feeSum.nextDue ? '#eee' : '#666' }}>
                          {feeSum.nextDue || '—'}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="admin-button"
                            style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                            onClick={() => {
                              setLinkedPlayersEditorParentId(null);
                              setFeeEditorParentId(feesOpen ? null : p.id);
                            }}
                          >
                            {feesOpen ? 'Close' : 'Edit'}
                          </button>
                        </td>
                        <td style={{ minWidth: '140px' }}>
                          <div style={{ fontSize: '0.8rem', color: linkedSummary ? '#ccc' : '#666', marginBottom: '6px' }}>
                            {linkedSummary || 'None linked'}
                          </div>
                          <button
                            type="button"
                            className="admin-button"
                            style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                            onClick={() => openLinkedPlayersEditor(p)}
                          >
                            {linkedOpen ? 'Close' : 'Link players'}
                          </button>
                        </td>
                        <td>
                          {hasProfile ? (
                            p.name || p.displayName || 'N/A'
                          ) : (
                            <span title="Stripe/checkout-only user — not a completed parent signup">
                              <em style={{ color: '#888' }}>No family profile</em>
                              {p.stripeCustomerId ? (
                                <span
                                  style={{
                                    display: 'block',
                                    fontSize: '0.75rem',
                                    fontFamily: 'ui-monospace, monospace',
                                    color: '#666',
                                    marginTop: '4px',
                                  }}
                                >
                                  {p.stripeCustomerId}
                                </span>
                              ) : null}
                            </span>
                          )}
                        </td>
                        <td>
                          {hasProfile ? (
                            p.email || 'N/A'
                          ) : (
                            <span
                              style={{
                                fontSize: '0.75rem',
                                fontFamily: 'ui-monospace, monospace',
                                color: '#666',
                                wordBreak: 'break-all',
                              }}
                              title="Firebase Auth UID"
                            >
                              {p.id}
                            </span>
                          )}
                        </td>
                      </tr>
                      {linkedOpen && (
                        <tr className="admin-fee-editor-row">
                          <td colSpan={9} style={{ background: '#12161c', verticalAlign: 'top', padding: '16px' }}>
                            <h3 style={{ margin: '0 0 8px', color: '#00a7a9', fontSize: '1.05rem' }}>
                              Linked roster players —{' '}
                              {p.name || p.displayName || p.email || p.id}
                            </h3>
                            <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '12px' }}>
                              Select the kids this parent may update on the public roster (photo, about, fun fact only).
                              Parents cannot add players themselves.
                            </p>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                gap: '8px',
                                marginBottom: '16px',
                                maxHeight: '280px',
                                overflowY: 'auto',
                              }}
                            >
                              {rosterLinkOptions.map((rp) => (
                                <label
                                  key={rp.key}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    color: '#ddd',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={linkedPlayersDraftKeys.includes(rp.key)}
                                    onChange={() => toggleLinkedPlayerDraftKey(rp.key)}
                                  />
                                  <span>{rp.label}</span>
                                </label>
                              ))}
                            </div>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="admin-button"
                                disabled={linkedPlayersSaving}
                                onClick={() => handleSaveLinkedPlayers(p.id)}
                              >
                                {linkedPlayersSaving ? 'Saving…' : 'Save linked players'}
                              </button>
                              <button
                                type="button"
                                className="admin-button"
                                style={{ background: 'transparent' }}
                                disabled={linkedPlayersSaving}
                                onClick={() => {
                                  setLinkedPlayersEditorParentId(null);
                                  setLinkedPlayersDraftKeys([]);
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {feesOpen && (
                        <tr className="admin-fee-editor-row">
                          <td colSpan={9} style={{ background: '#12161c', verticalAlign: 'top', padding: '16px' }}>
                            <div className="admin-fee-editor">
                              <h3 style={{ margin: '0 0 12px', color: '#00a7a9', fontSize: '1.05rem' }}>
                                Team dues line items —{' '}
                                {p.name || p.displayName || p.email || p.stripeCustomerId || p.id}
                              </h3>
                              <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: '12px' }}>
                                Each row is one charge. Set <strong style={{ color: '#aaa' }}>Due date</strong> so families know the deadline. Check <strong style={{ color: '#aaa' }}>Paid</strong> when money is received. Parents can upload a photo as payment proof; open the link in <strong style={{ color: '#aaa' }}>Parent proof</strong> to review.
                              </p>
                              {feeEditorLoading ? (
                                <p style={{ color: '#999' }}>Loading…</p>
                              ) : (
                                <>
                                  <div style={{ overflowX: 'auto' }}>
                                    <table className="admin-table" style={{ marginBottom: '16px', fontSize: '0.9rem' }}>
                                      <thead>
                                        <tr>
                                          <th>Description</th>
                                          <th>Amount ($)</th>
                                          <th>Season / note</th>
                                          <th>Due date</th>
                                          <th>Paid</th>
                                          <th>Paid date</th>
                                          <th>Payment note</th>
                                          <th>Parent proof</th>
                                          <th></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {feeEditorRows.length === 0 ? (
                                          <tr>
                                            <td colSpan={9} style={{ color: '#777' }}>No line items yet. Add the first charge below.</td>
                                          </tr>
                                        ) : (
                                          feeEditorRows.map((row) => (
                                            <tr key={row.id}>
                                              <td>
                                                <input
                                                  type="text"
                                                  className="admin-input"
                                                  value={row.description}
                                                  onChange={(e) => patchFeeRow(row.id, { description: e.target.value })}
                                                  style={{ minWidth: '140px' }}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  min="0"
                                                  className="admin-input"
                                                  value={Number.isFinite(Number(row.amount)) ? row.amount : ''}
                                                  onChange={(e) => {
                                                    const v = e.target.value;
                                                    const n = parseFloat(v);
                                                    patchFeeRow(row.id, { amount: v === '' || Number.isNaN(n) ? 0 : n });
                                                  }}
                                                  style={{ width: '96px' }}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="text"
                                                  className="admin-input"
                                                  placeholder="e.g. 2025–26"
                                                  value={row.seasonLabel}
                                                  onChange={(e) => patchFeeRow(row.id, { seasonLabel: e.target.value })}
                                                  style={{ minWidth: '100px' }}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="date"
                                                  className="admin-input"
                                                  value={row.dueDate || ''}
                                                  onChange={(e) => patchFeeRow(row.id, { dueDate: e.target.value })}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="checkbox"
                                                  checked={row.paid}
                                                  onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    patchFeeRow(row.id, {
                                                      paid: checked,
                                                      paidAt: checked ? (row.paidAt || new Date().toISOString().slice(0, 10)) : '',
                                                    });
                                                  }}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="date"
                                                  className="admin-input"
                                                  value={row.paidAt || ''}
                                                  disabled={!row.paid}
                                                  onChange={(e) => patchFeeRow(row.id, { paidAt: e.target.value })}
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="text"
                                                  className="admin-input"
                                                  placeholder="Venmo, check #…"
                                                  value={row.paidNote}
                                                  onChange={(e) => patchFeeRow(row.id, { paidNote: e.target.value })}
                                                  style={{ minWidth: '120px' }}
                                                />
                                              </td>
                                              <td style={{ maxWidth: '140px', fontSize: '0.8rem' }}>
                                                {row.paymentProofUrl ? (
                                                  <a
                                                    href={row.paymentProofUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#7fd0d2', wordBreak: 'break-all' }}
                                                  >
                                                    View proof
                                                  </a>
                                                ) : (
                                                  <span style={{ color: '#555' }}>—</span>
                                                )}
                                              </td>
                                              <td style={{ whiteSpace: 'nowrap' }}>
                                                <button type="button" className="admin-button" style={{ padding: '4px 10px', fontSize: '0.85rem' }} onClick={() => handleSaveFeeRow(row)}>
                                                  Save
                                                </button>
                                                <button
                                                  type="button"
                                                  className="admin-button-archive"
                                                  style={{ marginLeft: '6px', padding: '4px 10px', fontSize: '0.85rem' }}
                                                  onClick={() => handleDeleteFeeItem(row.id)}
                                                >
                                                  Delete
                                                </button>
                                              </td>
                                            </tr>
                                          ))
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end', marginBottom: '8px' }}>
                                    <div>
                                      <label style={{ display: 'block', color: '#999', fontSize: '0.75rem', marginBottom: '4px' }}>Description</label>
                                      <input
                                        type="text"
                                        className="admin-input"
                                        placeholder="e.g. Team warm-ups"
                                        value={feeNewDescription}
                                        onChange={(e) => setFeeNewDescription(e.target.value)}
                                        style={{ minWidth: '200px' }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', color: '#999', fontSize: '0.75rem', marginBottom: '4px' }}>Amount ($)</label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="admin-input"
                                        placeholder="0.00"
                                        value={feeNewAmount}
                                        onChange={(e) => setFeeNewAmount(e.target.value)}
                                        style={{ width: '100px' }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', color: '#999', fontSize: '0.75rem', marginBottom: '4px' }}>Season label (optional)</label>
                                      <input
                                        type="text"
                                        className="admin-input"
                                        placeholder="2025–26"
                                        value={feeNewSeason}
                                        onChange={(e) => setFeeNewSeason(e.target.value)}
                                        style={{ width: '120px' }}
                                      />
                                    </div>
                                    <div>
                                      <label style={{ display: 'block', color: '#999', fontSize: '0.75rem', marginBottom: '4px' }}>Due date (optional)</label>
                                      <input
                                        type="date"
                                        className="admin-input"
                                        value={feeNewDueDate}
                                        onChange={(e) => setFeeNewDueDate(e.target.value)}
                                        style={{ width: '140px' }}
                                      />
                                    </div>
                                    <button type="button" className="admin-button" onClick={handleAddFeeItem}>
                                      Add line item
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
          </>
      </div>
      )}

      {activeAdminSection === STAFF_SECTION_ID && allowedSectionSet.has(STAFF_SECTION_ID) && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">Staff accounts</h2>
        <p style={{ color: '#999', maxWidth: '720px', marginBottom: '16px' }}>
          Create Firebase Auth users for coaches and treasurers, and assign which areas of this portal they can open.
          Only super-admins (full access) see this section.
        </p>

        {staffAccountsNotice && (
          <div
            className={`admin-staff-notice admin-staff-notice--${
              staffAccountsNotice.variant === 'success' ? 'success' : 'error'
            }`}
            role={staffAccountsNotice.variant === 'error' ? 'alert' : 'status'}
            aria-live={staffAccountsNotice.variant === 'error' ? 'assertive' : 'polite'}
          >
            <span className="admin-staff-notice__text">{staffAccountsNotice.message}</span>
            <button
              type="button"
              className="admin-staff-notice-dismiss"
              onClick={() => setStaffAccountsNotice(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        <h3 style={{ color: '#ccc', fontSize: '1rem', margin: '20px 0 8px' }}>Add staff user</h3>
        <form onSubmit={handleCreateStaffUser} style={{ maxWidth: '640px', marginBottom: '24px' }}>
          <div className="admin-staff-form-fields">
            <input
              type="email"
              placeholder="Email"
              value={newStaffEmail}
              onChange={(e) => setNewStaffEmail(e.target.value)}
              className="admin-staff-form-fields__grow"
            />
            <div className="admin-password-with-toggle admin-staff-form-fields__grow">
              <input
                type={showNewStaffPassword ? 'text' : 'password'}
                placeholder="Initial password (min 6)"
                value={newStaffPassword}
                onChange={(e) => setNewStaffPassword(e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="admin-password-visibility-btn"
                onClick={() => setShowNewStaffPassword((v) => !v)}
                aria-pressed={showNewStaffPassword}
                aria-label={showNewStaffPassword ? 'Hide password' : 'Show password'}
                title={showNewStaffPassword ? 'Hide password' : 'Show password'}
              >
                {showNewStaffPassword ? (
                  <svg className="admin-password-visibility-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M12 6c3.79 0 7.17 2.13 8.82 5.5-.79 1.58-2.02 2.91-3.53 3.86l1.41 1.41A11.9 11.9 0 0021.99 12C20.31 8.13 16.79 6 12 6zm-8.82 5.5C4.69 14.87 8.07 17 12 17c1.03 0 2.02-.15 2.95-.43l1.59 1.59c-1.46.55-3.05.84-4.54.84-4.79 0-8.31-2.13-9.99-6a12 12 0 003.53-3.86L3.18 7.18A11.9 11.9 0 002.01 12zM12 9a3 3 0 013 3c0 .46-.11.89-.29 1.29L10.71 9.29c.4-.18.83-.29 1.29-.29zm-3 3a3 3 0 013-3c.46 0 .89.11 1.29.29L9.29 13.29A2.9 2.9 0 019 12zm.88 6.12l1.41 1.41 9.19-9.19-1.41-1.41-9.19 9.19z"
                    />
                  </svg>
                ) : (
                  <svg className="admin-password-visibility-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M12 9a3 3 0 100 6 3 3 0 000-6zm0-3c4.79 0 8.31 2.13 9.99 6-1.68 3.87-5.2 6-9.99 6s-8.31-2.13-9.99-6C3.69 8.13 7.21 6 12 6zm0 2C8.48 8 5.69 9.38 4.18 12c1.51 2.62 4.3 4 7.82 4s6.31-1.38 7.82-4C18.31 10.38 15.52 8 12 8z"
                    />
                  </svg>
                )}
              </button>
            </div>
            <input
              type="text"
              placeholder="Display name"
              value={newStaffDisplayName}
              onChange={(e) => setNewStaffDisplayName(e.target.value)}
              className="admin-staff-form-fields__grow"
            />
          </div>
          <label className="admin-staff-option-label">
            <input
              type="checkbox"
              checked={newStaffFullAccess}
              onChange={(e) => setNewStaffFullAccess(e.target.checked)}
            />
            <span>Full access (super-admin)</span>
          </label>
          {!newStaffFullAccess && (
            <div className="admin-staff-access-panel">
              <div className="admin-staff-access-grid">
                {assignableSectionIdsForForms.map((sid) => (
                  <label key={sid} className="admin-staff-access-item">
                    <input
                      type="checkbox"
                      checked={newStaffSectionPicks.has(sid)}
                      onChange={() => togglePick(setNewStaffSectionPicks)(sid)}
                    />
                    <span className="admin-staff-access-label">{sid}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <button type="submit" className="admin-button" disabled={staffFormBusy}>
            {staffFormBusy ? 'Saving…' : 'Create staff user'}
          </button>
        </form>

        <h3 style={{ color: '#ccc', fontSize: '1rem', margin: '20px 0 8px' }}>Existing staff</h3>
        {staffListLoading ? (
          <p style={{ color: '#999' }}>Loading…</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Active</th>
                  <th>Sections</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {staffList.map((u) => (
                  <tr key={u.uid}>
                    <td>{u.email || '—'}</td>
                    <td>{u.displayName || '—'}</td>
                    <td>{u.active ? 'Yes' : 'No'}</td>
                    <td style={{ maxWidth: '280px', fontSize: '0.8rem' }}>
                      {(u.sections || []).join(', ') || '—'}
                    </td>
                    <td>
                      <button type="button" className="admin-button" onClick={() => beginEditStaff(u)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editingStaffUid && (
          <div
            style={{
              marginTop: '20px',
              padding: '16px',
              border: '1px solid #555',
              borderRadius: '8px',
              maxWidth: '640px',
            }}
          >
            <h4 style={{ color: '#ccc', marginTop: 0 }}>
              Edit {editingStaffRecord?.email || editingStaffRecord?.displayName || editingStaffUid}
            </h4>
            <label className="admin-staff-option-label">
              <input
                type="checkbox"
                checked={editStaffActive}
                onChange={(e) => setEditStaffActive(e.target.checked)}
              />
              <span>Active</span>
            </label>
            <label className="admin-staff-option-label">
              <input
                type="checkbox"
                checked={editStaffFullAccess}
                onChange={(e) => setEditStaffFullAccess(e.target.checked)}
              />
              <span>Full access (super-admin)</span>
            </label>
            {!editStaffFullAccess && (
              <div className="admin-staff-access-panel">
                <div className="admin-staff-access-grid">
                  {assignableSectionIdsForForms.map((sid) => (
                    <label key={sid} className="admin-staff-access-item">
                      <input
                        type="checkbox"
                        checked={editStaffSectionPicks.has(sid)}
                        onChange={() => togglePick(setEditStaffSectionPicks)(sid)}
                      />
                      <span className="admin-staff-access-label">{sid}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="admin-staff-password-row">
              <span className="admin-staff-password-row__label">New password (optional)</span>
              <div className="admin-password-with-toggle admin-password-with-toggle--block">
                <input
                  type={showEditStaffPassword ? 'text' : 'password'}
                  placeholder="Leave blank to keep current"
                  value={editStaffPassword}
                  onChange={(e) => setEditStaffPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="admin-password-visibility-btn"
                  onClick={() => setShowEditStaffPassword((v) => !v)}
                  aria-pressed={showEditStaffPassword}
                  aria-label={showEditStaffPassword ? 'Hide password' : 'Show password'}
                  title={showEditStaffPassword ? 'Hide password' : 'Show password'}
                >
                  {showEditStaffPassword ? (
                    <svg className="admin-password-visibility-icon" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M12 6c3.79 0 7.17 2.13 8.82 5.5-.79 1.58-2.02 2.91-3.53 3.86l1.41 1.41A11.9 11.9 0 0021.99 12C20.31 8.13 16.79 6 12 6zm-8.82 5.5C4.69 14.87 8.07 17 12 17c1.03 0 2.02-.15 2.95-.43l1.59 1.59c-1.46.55-3.05.84-4.54.84-4.79 0-8.31-2.13-9.99-6a12 12 0 003.53-3.86L3.18 7.18A11.9 11.9 0 002.01 12zM12 9a3 3 0 013 3c0 .46-.11.89-.29 1.29L10.71 9.29c.4-.18.83-.29 1.29-.29zm-3 3a3 3 0 013-3c.46 0 .89.11 1.29.29L9.29 13.29A2.9 2.9 0 019 12zm.88 6.12l1.41 1.41 9.19-9.19-1.41-1.41-9.19 9.19z"
                      />
                    </svg>
                  ) : (
                    <svg className="admin-password-visibility-icon" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M12 9a3 3 0 100 6 3 3 0 000-6zm0-3c4.79 0 8.31 2.13 9.99 6-1.68 3.87-5.2 6-9.99 6s-8.31-2.13-9.99-6C3.69 8.13 7.21 6 12 6zm0 2C8.48 8 5.69 9.38 4.18 12c1.51 2.62 4.3 4 7.82 4s6.31-1.38 7.82-4C18.31 10.38 15.52 8 12 8z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="admin-staff-edit-actions">
              <button type="button" className="admin-button" disabled={staffFormBusy} onClick={handleSaveEditStaff}>
                Save
              </button>
              <button
                type="button"
                className="admin-button-restore"
                disabled={staffFormBusy}
                onClick={() => setEditingStaffUid(null)}
              >
                Cancel
              </button>
            </div>
            <div className="admin-staff-remove-section">
              <p className="admin-staff-remove-section__hint">
                Permanently remove this person&apos;s staff login and portal access.
              </p>
              <button
                type="button"
                className="admin-button-danger"
                disabled={
                  staffFormBusy || (auth.currentUser && auth.currentUser.uid === editingStaffUid)
                }
                onClick={handleRemoveStaff}
              >
                Remove staff member
              </button>
              {auth.currentUser?.uid === editingStaffUid && (
                <p className="admin-staff-remove-section__self">You cannot remove your own account.</p>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {activeAdminSection === 'gallery' && allowedSectionSet.has('gallery') && (
      <div className="admin-section">
        <h2 className="admin-main-section-title">
          🖼️ Manage Uploaded Photos
          {gallery.length > 0 && <span className="admin-section-count"> ({gallery.length})</span>}
        </h2>
        {gallery.length === 0 ? (
          <p>No photos uploaded yet.</p>
        ) : (
          <div className="photo-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
            {gallery.map(photo => (
              <div key={photo.id} style={{ backgroundColor: '#111', padding: '8px', borderRadius: '8px' }}>
                <img src={photo.imageUrl} alt={photo.caption} style={{ width: '100%', borderRadius: '6px', marginBottom: '8px' }} />
                <p style={{ color: '#ccc', margin: 0 }}>{photo.caption}</p>
                <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '8px' }}>{photo.tag}</p>
                <button
                  onClick={() => handleDeleteImage(photo.id, photo.imageUrl)}
                  style={{
                    backgroundColor: '#B4975A',
                    color: 'black',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 10px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
        </main>
      </div>
    </div>
  );
};

export default AdminPortal;